import React, { useState, useEffect, useLayoutEffect, useMemo, useRef, useCallback } from 'react';
import { formatCurrency } from '../utils/currency';
import { formatCountdown } from '../utils/formatCountdown';
import ShoppingList from './ShoppingList';
import CatalogExplorer from './CatalogExplorer';
import SpatialMoodboard from './SpatialMoodboard';
import ModalCloseButton from './ui/ModalCloseButton';
import PlanEventDetailsModal from './PlanEventDetailsModal';
import ColorPaletteEditModal from './ColorPaletteEditModal';
import DigitalInviteModal from './DigitalInviteModal';
import BulkCartExportModal from './BulkCartExportModal';
import SocialStoryModal from './SocialStoryModal';
import { getThemePlanKey, getConceptLabelForIntent } from '../utils/themePlanKeys';
import { getPlanningIntentConfig, getAllowedPlanSections, getPrimaryPlanningIntent } from '../utils/planningIntent';
import { needsPlanReEstimate } from '../utils/planEstimateCore';
import { isValidHex, normalizePalette } from '../utils/colorPalette';
import { getColorNameFromHex } from '../utils/colorNames';
import * as sharingUtils from '../utils/sharing';
import { trackEvent } from '../utils/telemetry';

const NotIncludedState = ({ title }) => (
  <div className="flex flex-col items-center justify-center p-8 text-center bg-gray-50 border border-gray-200 rounded-2xl max-w-lg mx-auto my-8">
    <span className="text-4xl mb-4" role="img" aria-label="Folder">📁</span>
    <h4 className="text-lg font-bold text-gray-950 mb-2">{title} Not Included</h4>
    <p className="text-sm text-gray-600 leading-relaxed">
      This section was not included in your planning focus selection when this plan was generated.
    </p>
  </div>
);

const MissingDataState = ({ title }) => (
  <div className="flex flex-col items-center justify-center p-8 text-center bg-gray-50 border border-gray-200 rounded-2xl max-w-lg mx-auto my-8">
    <span className="text-4xl mb-4" role="img" aria-label="Wind">💨</span>
    <h4 className="text-lg font-bold text-gray-950 mb-2">No {title} Data Available</h4>
    <p className="text-sm text-gray-600 leading-relaxed">
      No custom {title.toLowerCase()} details were generated for this plan version.
    </p>
  </div>
);

const METRIC_GRID_GAP_PX = 6;
const METRIC_TILE_PADDING_PX = 20;
const METRIC_WIDTH_BUFFER_PX = 10;
const METRIC_MIN_COL_WIDTH_PX = 72;
const METRIC_LABEL_FONT = '700 10px ui-sans-serif, system-ui, sans-serif';
const METRIC_VALUE_FONT = '800 12px ui-sans-serif, system-ui, sans-serif';
const METRIC_SUB_FONT = '600 10px ui-sans-serif, system-ui, sans-serif';
const QUICK_FACTS_COL_PREFERENCE = [3, 2, 6, 4, 5];

const normalizeMealTypeLabel = (label) => {
  if (!label) return '';
  const clean = label.trim().toLowerCase();
  if (clean === 'dinner items' || clean === 'dinner' || clean === 'main' || clean === 'main course') {
    return 'Main Course';
  }
  if (clean === 'appetizers' || clean === 'appetizer') {
    return 'Appetizer';
  }
  if (clean === 'desserts' || clean === 'dessert') {
    return 'Dessert';
  }
  if (clean === 'snacks' || clean === 'snack') {
    return 'Snack';
  }
  if (clean === 'drinks' || clean === 'drink' || clean === 'cocktails' || clean === 'cocktail') {
    return 'Drink';
  }
  if (clean === 'cake') {
    return 'Cake';
  }
  return label.charAt(0).toUpperCase() + label.slice(1);
};

const getMealType = (item) => {
  if (!item) return 'Other';
  if (item.meal_type) {
    const norm = normalizeMealTypeLabel(item.meal_type);
    if (norm && norm.toLowerCase() !== 'food & cake') {
      return norm;
    }
  }
  // Try to infer from name / description
  const text = `${item.name || ''} ${item.description || ''}`.toLowerCase();
  if (text.includes('dinner') || text.includes('main') || text.includes('entree') || text.includes('entrée') || text.includes('course') || text.includes('platter')) {
    return 'Main Course';
  }
  if (text.includes('appetizer') || text.includes('starter') || text.includes('bite') || text.includes('charcuterie') || text.includes('dip') || text.includes('skewer')) {
    return 'Appetizer';
  }
  if (text.includes('dessert') || text.includes('sweet') || text.includes('chocolate') || text.includes('cookie') || text.includes('cupcake') || text.includes('pastry') || text.includes('tart') || text.includes('pie')) {
    return 'Dessert';
  }
  if (text.includes('cake')) {
    return 'Cake';
  }
  if (text.includes('snack') || text.includes('popcorn') || text.includes('nut') || text.includes('chip') || text.includes('pretzel')) {
    return 'Snack';
  }
  if (text.includes('drink') || text.includes('beverage') || text.includes('cocktail') || text.includes('mocktail') || text.includes('punch') || text.includes('juice') || text.includes('soda')) {
    return 'Drink';
  }
  return 'Other';
};

let metricMeasureCanvas = null;

const measureTextWidth = (text, font) => {
  if (typeof document === 'undefined') return String(text).length * 7;
  if (!metricMeasureCanvas) {
    metricMeasureCanvas = document.createElement('canvas');
  }
  const ctx = metricMeasureCanvas.getContext('2d');
  if (!ctx) return String(text).length * 7;
  ctx.font = font;
  return ctx.measureText(String(text ?? '')).width;
};

const columnWidthAtCount = (containerWidth, cols) => (
  (containerWidth - (cols - 1) * METRIC_GRID_GAP_PX) / cols
);

const getViableColumnCounts = (containerWidth, itemCount, minCellWidth, minCols = 2) => {
  if (!containerWidth || containerWidth <= 0 || itemCount <= 0) return [Math.min(itemCount, minCols)];
  const viable = [];
  for (let cols = Math.min(itemCount, minCols); cols <= itemCount; cols += 1) {
    if (columnWidthAtCount(containerWidth, cols) >= minCellWidth) {
      viable.push(cols);
    }
  }
  return viable.length ? viable : [minCols];
};

const pickColumnCount = (viableCounts, preferredOrder = null) => {
  if (!viableCounts.length) return 2;
  if (preferredOrder?.length) {
    const preferred = preferredOrder.find((cols) => viableCounts.includes(cols));
    if (preferred) return preferred;
  }
  return viableCounts[viableCounts.length - 1];
};

const getMetricGridColumnCount = (
  containerWidth,
  itemCount,
  minCellWidth,
  minCols = 2,
  preferredOrder = null
) => {
  const viable = getViableColumnCounts(containerWidth, itemCount, minCellWidth, minCols);
  return pickColumnCount(viable, preferredOrder);
};

const estimateQuickFactCellMinWidth = (facts = []) => facts.reduce((max, fact) => {
  const labelW = measureTextWidth(fact.label, METRIC_LABEL_FONT);
  const valueW = measureTextWidth(fact.value, METRIC_VALUE_FONT);
  const cellW = Math.max(labelW, valueW, 48) + METRIC_TILE_PADDING_PX + METRIC_WIDTH_BUFFER_PX;
  return Math.max(max, cellW);
}, METRIC_MIN_COL_WIDTH_PX);

const estimatePlanMetricCellMinWidth = ({ budget, budgetSub, shopping, shoppingSub, catalog, catalogSub }) => {
  const labelFont = METRIC_LABEL_FONT;
  const valueFont = METRIC_VALUE_FONT;
  const subFont = METRIC_SUB_FONT;
  const widths = [
    Math.max(measureTextWidth('Budget', labelFont), measureTextWidth(budget, valueFont), measureTextWidth(budgetSub, subFont)),
    Math.max(measureTextWidth('Shopping list', labelFont), measureTextWidth(shopping, valueFont), measureTextWidth(shoppingSub, subFont)),
    Math.max(measureTextWidth('Catalog', labelFont), measureTextWidth(catalog, valueFont), measureTextWidth(catalogSub, subFont))
  ];
  return Math.max(...widths, 52) + METRIC_TILE_PADDING_PX;
};

const useMetricGridColumns = (
  containerRef,
  itemCount,
  minCellWidth,
  minCols = 2,
  { preferredOrder = null, verifyNoWrap = false } = {}
) => {
  const [columnCount, setColumnCount] = useState(() => Math.min(itemCount, minCols));

  useEffect(() => {
    const node = containerRef.current;
    if (!node) return undefined;

    const update = () => {
      const width = node.getBoundingClientRect().width;
      let cols = getMetricGridColumnCount(width, itemCount, minCellWidth, minCols, preferredOrder);

      const applyColumns = (nextCols) => {
        cols = Math.min(itemCount, Math.max(minCols, nextCols));
        setColumnCount(cols);
      };

      applyColumns(cols);

      if (!verifyNoWrap) return;

      const tightenIfWrapped = () => {
        const values = node.querySelectorAll('[data-metric-value]');
        const hasWrap = Array.from(values).some(
          (el) => el.scrollWidth > el.clientWidth + 1 || el.scrollHeight > el.clientHeight + 1
        );
        if (!hasWrap || cols <= minCols) return;

        const viable = getViableColumnCounts(width, itemCount, minCellWidth, minCols).filter((c) => c < cols);
        const next = viable.length ? pickColumnCount(viable, preferredOrder) : cols - 1;
        if (next >= cols) return;

        applyColumns(next);
        requestAnimationFrame(tightenIfWrapped);
      };

      requestAnimationFrame(tightenIfWrapped);
    };

    update();

    if (typeof ResizeObserver === 'undefined') return undefined;

    const observer = new ResizeObserver(() => update());
    observer.observe(node);
    return () => observer.disconnect();
  }, [containerRef, itemCount, minCellWidth, minCols, preferredOrder, verifyNoWrap]);

  return columnCount;
};

const Plan = ({ theme, eventData, items, setItems, onBackToThemes, onTryDifferent, onThemeRefined, onShare, planId, themePlans = {}, onViewThemePlan, onSaveCoreEventDetails, onReEstimatePlan, onSavePlan, savePlanEnabled = false, previousView = 'dashboard', sharePermissions = null, isSharedView = false, onItemLinksChanged, isFavorite = false, onToggleFavorite = null, initialActiveTab = 'overview', onActiveTabChange = null, onResumePlanning }) => {
  const rawCriticScore = theme?.critic_score;
  const parsedCriticScore =
    rawCriticScore !== null &&
    rawCriticScore !== undefined &&
    String(rawCriticScore).trim() !== ''
      ? Number(rawCriticScore)
      : NaN;

  const hasValidScore =
    Number.isFinite(parsedCriticScore) &&
    parsedCriticScore >= 0 &&
    parsedCriticScore <= 10;

  // Strip markdown formatting (* ** __ ## etc) from AI-generated text
  const stripMarkdown = (text) => {
    if (typeof text !== 'string') return '';
    return text
      .replace(/\*\*(.*?)\*\*/g, '$1')  // **bold**
      .replace(/\*(.*?)\*/g, '$1')       // *italic*
      .replace(/__(.*?)__/g, '$1')       // __underline__
      .replace(/_(.*?)_/g, '$1')         // _italic_
      .replace(/^#{1,6}\s+/gm, '')       // ## headings
      .replace(/^\s*[-*+]\s+/gm, '')     // list bullets (* - +)
      .replace(/`{1,3}(.*?)`{1,3}/g, '$1') // `code`
      .replace(/\[([^\]]+)\]\([^)]+\)/g, '$1') // [links](url)
      .trim();
  };

  const plannerReview = typeof theme?.reasoning?.planner_review === 'string'
    ? stripMarkdown(theme.reasoning.planner_review)
    : '';

  const plannerBadges = Array.isArray(theme?.planner_badges)
    ? theme.planner_badges.filter((badge) => typeof badge === 'string' && badge.trim()).map(stripMarkdown).slice(0, 3)
    : [];

  const hasReviewData = hasValidScore || !!plannerReview || plannerBadges.length > 0;

  const hasProvidedBudget = eventData?.budget !== null && eventData?.budget !== undefined && eventData?.budget !== '' && Number(eventData.budget) > 0;
  const hasProvidedGuestCount = eventData?.guest_count !== null && eventData?.guest_count !== undefined && eventData?.guest_count !== '' && Number(eventData.guest_count) > 0;
  const primaryIntent = getPrimaryPlanningIntent(eventData?.focus_selection);
  const isThemeIdeas = primaryIntent === 'theme_ideas';
  const isConceptFirst = useMemo(() => {
    const focus = eventData?.focus_selection || [];
    const isFoodOnly = focus.length === 1 && focus[0] === 'food';
    const isDecorOnly = focus.length === 1 && focus[0] === 'decor';
    return focus.includes('everything') || focus.includes('theme_ideas') || isFoodOnly || isDecorOnly;
  }, [eventData?.focus_selection]);

  const hasSelectedTheme = Boolean(theme && (theme.name || theme.title || theme.id));
  const hasThemeConcepts = Boolean(themePlans && Object.keys(themePlans).length > 0);

  const hasThemeDetails = Boolean(
    theme &&
    theme.categories &&
    Object.keys(theme.categories).length > 0 &&
    Object.values(theme.categories).some(cat => Array.isArray(cat) && cat.length > 0)
  );

  const hasShoppingItems = Array.isArray(items) && items.length > 0;
  const hasTimeline = Array.isArray(theme?.timeline) && theme.timeline.length > 0;
  const hasMenu = Array.isArray(theme?.menu) && theme.menu.length > 0;

  const isThemeIdeasOnlyPlan = Boolean(
    eventData?.focus_selection &&
    eventData.focus_selection.includes('theme_ideas') &&
    !eventData.focus_selection.includes('everything') &&
    !eventData.focus_selection.some(f => ['decor', 'food', 'activities', 'shopping'].includes(f))
  );

  const isDirectPlan = Boolean(
    theme?.catalog_quick_workflow ||
    (eventData?.focus_selection &&
     !eventData.focus_selection.includes('everything') &&
     !eventData.focus_selection.includes('theme_ideas') &&
     eventData.focus_selection.some(f => ['decor', 'food', 'activities', 'shopping'].includes(f)))
  );

  const isIncompletePlan = (!hasSelectedTheme || (!hasThemeDetails && !hasShoppingItems && !hasTimeline && !isThemeIdeasOnlyPlan)) && !isDirectPlan;

  const budgetSummary = hasProvidedBudget
    ? formatCurrency(eventData.budget, eventData?.country || 'US')
    : 'Budget not set';
  const perGuestSummary = hasProvidedGuestCount && hasProvidedBudget
    ? formatCurrency(Math.round(Number(eventData.budget) / Number(eventData.guest_count)), eventData?.country || 'US')
    : null;
  // Tab state - the plan opens as a planner hub, with shopping as one section.
  const [activeTab, setActiveTabState] = useState(initialActiveTab);
  const shouldScrollToTabContentRef = useRef(false);
  const setActiveTab = useCallback((tab) => {
    shouldScrollToTabContentRef.current = true;
    setActiveTabState(tab);
    trackEvent('plan_tab_switched', {
      tab,
      theme: theme?.name || theme?.theme_name || 'unknown'
    });
    onActiveTabChange?.(tab);
  }, [onActiveTabChange, theme]);
  useEffect(() => {
    setActiveTabState(initialActiveTab);
  }, [initialActiveTab]);
  useEffect(() => {
    window.__planComponentMounted = true;
    window.__onViewThemePlan = onViewThemePlan;
    window.__currentTheme = theme;
    return () => {
      window.__planComponentMounted = false;
    };
  }, [onViewThemePlan, theme]);
  const tabContentRef = useRef(null);
  const planTabsRef = useRef(null);
  const planTabStripRef = useRef(null);
  const planTabContentTopRef = useRef(null);
  const planVersionsStripRef = useRef(null);
  const quickFactsGridRef = useRef(null);
  const planMetricsGridRef = useRef(null);

  useEffect(() => {
    if (!shouldScrollToTabContentRef.current) return;
    shouldScrollToTabContentRef.current = false;

    if (typeof window === 'undefined') return;
    if (window.innerWidth >= 1024) return;

    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        const main = document.querySelector("main[data-app-main-scroll='true']");
        const anchor = planTabContentTopRef.current;
        if (!anchor) return;

        const tabsEl = planTabsRef.current;
        const tabsHeight = tabsEl ? tabsEl.getBoundingClientRect().height : 50;
        const stickyOffset = window.innerWidth < 768 ? 120 : 96;
        const extraGap = 8; // Small gap for visual spacing below the tab bar

        if (main) {
          const mainRect = main.getBoundingClientRect();
          const anchorRect = anchor.getBoundingClientRect();
          const currentScrollTop = main.scrollTop;
          const targetScrollTop = currentScrollTop + (anchorRect.top - mainRect.top) - stickyOffset - tabsHeight - extraGap;

          main.scrollTo({
            top: Math.max(0, targetScrollTop),
            behavior: 'smooth'
          });
        } else {
          const anchorRect = anchor.getBoundingClientRect();
          const targetY = window.scrollY + anchorRect.top - stickyOffset - tabsHeight - extraGap;
          window.scrollTo({
            top: Math.max(0, targetY),
            behavior: 'smooth'
          });
        }
      });
    });
  }, [activeTab]);
  const [showEventDetailsModal, setShowEventDetailsModal] = useState(false);
  const [isWideLayout, setIsWideLayout] = useState(false);

  // Note: we intentionally do NOT scroll the page to the top when switching plan
  // tabs. The plan's tab bar is sticky, so the user keeps their visual anchor;
  // jumping to the top of the page is disorienting and was a long-standing
  // complaint. Initial scroll-to-top for screen navigation is handled by AppLayout.

  // Expanded categories state - lifted from ShoppingList to persist across tab switches
  const [expandedCategories, setExpandedCategories] = useState(() => {
    // Default: all categories collapsed (none expanded by default)
    const categories = Object.keys(theme?.categories || {});
    const init = {};
    categories.forEach((c) => { init[c] = false; }); // All collapsed by default
    return init;
  });

  // Update expandedCategories when theme changes (in case categories change)
  useEffect(() => {
    const categories = Object.keys(theme?.categories || {});
    setExpandedCategories(prev => {
      const updated = { ...prev };
      // Add new categories (collapsed by default)
      categories.forEach(cat => {
        if (updated[cat] === undefined) {
          updated[cat] = false;
        }
      });
      // Remove categories that no longer exist
      Object.keys(updated).forEach(cat => {
        if (!categories.includes(cat)) {
          delete updated[cat];
        }
      });
      return updated;
    });
  }, [theme?.categories]);

  // Reasoning modal state
  const [showReasoningModal, setShowReasoningModal] = useState(false);

  // Color editing state (defined early so it can be used in useEffect)
  const [showPaletteEditModal, setShowPaletteEditModal] = useState(false);
  const [showCatalogModal, setShowCatalogModal] = useState(false);
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [showBulkCartModal, setShowBulkCartModal] = useState(false);
  const [showSocialStoryModal, setShowSocialStoryModal] = useState(false);
  const [catalogLaunch, setCatalogLaunch] = useState({ tab: 'items', category: '', search: '' });
  const [quickShareUrl, setQuickShareUrl] = useState('');
  const [shareLookupDone, setShareLookupDone] = useState(false);

  const openCatalogExplorer = ({ tab = 'items', category = '', search = '' } = {}) => {
    setCatalogLaunch({ tab, category, search });
    setShowCatalogModal(true);
  };

  // Color palette state (draft until saved, matching theme editor behavior)
  const [colorPalette, setColorPalette] = useState(() => normalizePalette(theme?.color_palette));
  const paletteSignature = useMemo(
    () => JSON.stringify(theme?.color_palette || []),
    [theme?.color_palette]
  );

  useEffect(() => {
    let parsedPalette = [];
    try {
      parsedPalette = JSON.parse(paletteSignature);
    } catch (_) {
      parsedPalette = [];
    }
    const normalized = normalizePalette(parsedPalette);
    setColorPalette(normalized);
  }, [theme?.id, theme?.name, paletteSignature]);

  useEffect(() => {
    let isMounted = true;
    setQuickShareUrl('');
    setShareLookupDone(false);
    if (!planId || isSharedView) {
      setShareLookupDone(true);
      return () => {
        isMounted = false;
      };
    }

    sharingUtils.listPlanShares(planId)
      .then((shares) => {
        if (!isMounted) return;
        const activeLink = (shares || []).find((share) => share.share_type === 'link' && share.is_active !== false);
        setQuickShareUrl(activeLink?.share_token ? sharingUtils.getShareUrl(activeLink.share_token) : '');
      })
      .catch(() => {
        if (isMounted) setQuickShareUrl('');
      })
      .finally(() => {
        if (isMounted) setShareLookupDone(true);
      });

    return () => {
      isMounted = false;
    };
  }, [planId, isSharedView]);

  // Hide stepper when any modal is open
  useEffect(() => {
    const hasModalOpen = showReasoningModal || showPaletteEditModal || showCatalogModal;
    if (hasModalOpen) {
      document.body.classList.add('modal-open');
    } else {
      document.body.classList.remove('modal-open');
    }
    return () => {
      document.body.classList.remove('modal-open');
    };
  }, [showReasoningModal, showPaletteEditModal, showCatalogModal]);

  // Handle Escape key to close reasoning modal
  useEffect(() => {
    if (!showReasoningModal) return;
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        setShowReasoningModal(false);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [showReasoningModal]);

  // Soften any prescriptive AI-generated copy that sounds bossy.
  const softenDressCodeText = (text) => {
    if (!text) return '';
    let out = String(text);
    out = out.replace(/\bguests?\s+must\s+wear(?:\s+only)?\b/gi, 'guests are encouraged to wear');
    out = out.replace(/\bmust\s+wear(?:\s+only)?\b/gi, 'are encouraged to wear');
    out = out.replace(/\bonly\s+wear\b/gi, 'lean toward');
    out = out.replace(/\brequired\s+to\s+wear\b/gi, 'invited to wear');
    out = out.replace(/\byou\s+must\b/gi, 'we suggest you');
    return out;
  };

  // Dress code state (editable) - supports both old and new format
  const [dressCode, setDressCode] = useState(() => {
    const dc = theme?.dress_code;
    // Limit default suggested colors to 3 — the user can add more from the edit modal.
    const defaultPaletteColors = normalizePalette(theme?.color_palette)
      .filter((color) => isValidHex(color.hex))
      .map((color) => color.hex)
      .slice(0, 3);
    if (!dc) {
      return {
        adults: {
          style: 'Smart casual',
          description: 'Comfortable, polished outfits that work across styles and genders — think tailored separates, dresses, jumpsuits, or neat casual layers.',
          colors: defaultPaletteColors
        },
        teens: { style: 'Trendy casual', description: 'Stylish and comfortable', colors: defaultPaletteColors },
        kids: { style: 'Comfortable play clothes', description: 'Easy to move and play in', colors: defaultPaletteColors }
      };
    }
    const normalizeGroup = (group, fallbackStyle, fallbackDescription) => ({
      style: group?.style || fallbackStyle,
      description: softenDressCodeText(group?.description || fallbackDescription),
      style_type: group?.style_type || (group?.style_guide ? 'theme' : 'colors'),
      style_guide: group?.style_guide ? softenDressCodeText(group.style_guide) : '',
      colors: Array.isArray(group?.colors) && group.colors.length > 0
        ? group.colors.slice(0, 6)
        : defaultPaletteColors
    });
    if (dc.adults || dc.teens || dc.kids) {
      return {
        adults: normalizeGroup(dc.adults, 'Smart casual', ''),
        teens: normalizeGroup(dc.teens, 'Trendy casual', 'Stylish and comfortable'),
        kids: normalizeGroup(dc.kids, 'Comfortable play clothes', 'Easy to move and play in')
      };
    }
    // Convert old (flat) format to per-age-group format.
    const sharedColors = Array.isArray(dc.suggested_colors) && dc.suggested_colors.length > 0
      ? dc.suggested_colors.slice(0, 6)
      : defaultPaletteColors;
    return {
      adults: { style: dc.style || 'Smart casual', description: softenDressCodeText(dc.description || ''), style_type: 'colors', style_guide: '', colors: sharedColors },
      teens: { style: dc.style || 'Smart casual', description: softenDressCodeText(dc.description || ''), style_type: 'colors', style_guide: '', colors: sharedColors },
      kids: { style: 'Comfortable play clothes', description: 'Easy to move and play in', style_type: 'colors', style_guide: '', colors: sharedColors }
    };
  });
  const [editingAgeGroup, setEditingAgeGroup] = useState(null); // 'adults', 'teens', 'kids'
  const [dressCodeDraft, setDressCodeDraft] = useState({ ...dressCode });

  // Find items using a specific color
  const displayedPalette = colorPalette.filter((color) => isValidHex(color.hex));

  const handlePaletteSave = (savedPalette) => {
    setColorPalette(savedPalette);
    // Reset dress code suggested colors to the top 3 of the new theme palette.
    // Users can still customize dress code colors per age group from the dress code editor.
    const paletteHexList = savedPalette.map((color) => color.hex).slice(0, 3);
    const nextDressCode = {
      adults: { ...dressCode.adults, colors: paletteHexList },
      teens: { ...dressCode.teens, colors: paletteHexList },
      kids: { ...dressCode.kids, colors: paletteHexList }
    };
    setDressCode(nextDressCode);
    onThemeRefined?.({ ...theme, color_palette: savedPalette, dress_code: nextDressCode });
  };

  // Get color name from hex
  const getColorName = (hex) => {
    const color = colorPalette.find(c => c.hex === hex);
    return color ? color.name : hex;
  };

  const normalizeAgeRangeText = (value) => {
    if (Array.isArray(value)) return value.join(' ').toLowerCase();
    return String(value || '').toLowerCase();
  };

  const hasDressCodeContent = (groupKey) => {
    const group = dressCode?.[groupKey];
    if (!group) return false;
    const hasText = !!(String(group.style || '').trim() || String(group.description || '').trim());
    const hasColors = Array.isArray(group.colors) && group.colors.some((hex) => String(hex || '').trim());
    return hasText || hasColors;
  };

  // Determine which age groups to show based on age_range
  const getVisibleAgeGroupsByRange = () => {
    const ageText = normalizeAgeRangeText(eventData?.age_range);
    let showAdults = true;
    let showTeens = true;
    let showKids = true;

    if (ageText) {
      if (ageText.includes('mixed')) {
        // keep all groups
      } else if (ageText.includes('kids') || ageText.includes('0-12')) {
        showAdults = false;
        showTeens = false;
        showKids = true;
      } else if (ageText.includes('teen') || ageText.includes('13-17')) {
        showAdults = false;
        showTeens = true;
        showKids = false;
      } else if (
        ageText.includes('adult')
        || ageText.includes('young adult')
        || ageText.includes('18-29')
        || ageText.includes('30-49')
        || ageText.includes('50+')
      ) {
        showAdults = true;
        showTeens = false;
        showKids = false;
      }
    }
    return { showAdults, showTeens, showKids };
  };

  const getVisibleAgeGroups = () => {
    const range = getVisibleAgeGroupsByRange();
    return {
      showAdults: (range.showAdults && hasDressCodeContent('adults')) || editingAgeGroup === 'adults',
      showTeens: (range.showTeens && hasDressCodeContent('teens')) || editingAgeGroup === 'teens',
      showKids: (range.showKids && hasDressCodeContent('kids')) || editingAgeGroup === 'kids'
    };
  };

  const dressPaletteHexes = useMemo(() => {
    const fromDress = [
      ...(dressCode?.adults?.colors || []),
      ...(dressCode?.teens?.colors || []),
      ...(dressCode?.kids?.colors || [])
    ].filter(Boolean);
    const merged = [...fromDress, ...displayedPalette.map((color) => color.hex)];
    return [...new Set(merged)].slice(0, 3);
  }, [dressCode, displayedPalette]);

  const { showAdults, showTeens, showKids } = getVisibleAgeGroups();
  const visibleCount = [showAdults, showTeens, showKids].filter(Boolean).length;
  const itemCount = Array.isArray(items) ? items.length : 0;
  const guestCount = eventData?.guest_count || eventData?.guests || 0;
  const hasProvidedEventType = eventData?.event_type && eventData.event_type.trim() !== '';
  const eventType = hasProvidedEventType ? eventData.event_type : 'Event type not set';
  const eventTypeDescriptive = hasProvidedEventType ? eventData.event_type : 'event';
  const eventTypeDisplay = hasProvidedEventType ? eventData.event_type : 'Event type not set';
  const ageRange = eventData?.age_range || 'mixed audience';
  const venueLabel = eventData?.venue_type || eventData?.venue || 'your venue';
  const eventDateValue = eventData?.date || eventData?.event_date || eventData?.eventDate || null;
  const shoppedCount = Array.isArray(items)
    ? items.filter((item) => item?.purchased || item?.checked).length
    : 0;
  const eventCountdown = eventDateValue ? formatCountdown(eventDateValue) : null;
  const moneySpent = Number(eventData?.money_spent) || 0;
  const parseEventDate = (value) => {
    if (!value) return null;
    const parsed = new Date(`${value}T12:00:00`);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  };
  const eventDate = parseEventDate(eventDateValue);
  const formatDateLabel = (dateValue) => {
    if (!dateValue) return '';
    return dateValue.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
  };
  const dateForOffset = (daysBefore) => {
    if (!eventDate || typeof daysBefore !== 'number') return '';
    const date = new Date(eventDate);
    date.setDate(date.getDate() - daysBefore);
    return formatDateLabel(date);
  };
  const inferTimelineOffset = (timeText, index) => {
    const text = String(timeText || '').toLowerCase();
    if (text.includes('2-3 week') || text.includes('3 week')) return 21;
    if (text.includes('2 week')) return 14;
    if (text.includes('1 week') || text.includes('week before')) return 7;
    if (text.includes('day before')) return 1;
    if (text.includes('event day') || text.includes('day of')) return 0;
    return [21, 7, 1, 0][index] ?? null;
  };
  const getConceptLabelForRationale = () => {
    if (theme?.catalog_quick_workflow || isDirectPlan) return 'Plan';
    const primary = getPrimaryPlanningIntent(eventData?.focus_selection);
    if (primary === 'food') return 'Menu Concept';
    if (primary === 'decor') return 'Decor Concept';
    if (primary === 'theme_ideas' || primary === 'everything') return 'Theme';
    return 'Concept';
  };
  const themeReasoning = theme?.reasoning || {};
  const budgetGuardrail = theme?.budget_guardrail || {};
  const budgetStatusLabel = !hasProvidedBudget
    ? 'Not set'
    : budgetGuardrail.status === 'over_budget'
      ? 'Needs trimming'
      : budgetGuardrail.status === 'slightly_over_budget'
        ? 'Slightly over'
        : budgetGuardrail.status === 'within_budget'
        ? 'Budget aligned'
        : 'Budget aware';

  const fitInsights = [
    {
      label: 'Budget fit',
      body: themeReasoning.budget_fit || (hasProvidedBudget ? `${theme?.catalog_quick_workflow ? 'This plan' : (theme?.name || 'This plan')} is planned around your ${budgetSummary} budget with prioritized must-haves and optional upgrades kept separate.` : `${theme?.catalog_quick_workflow ? 'This plan' : (theme?.name || 'This plan')} is designed with budget-conscious defaults and structured shopping recommendations.`)
    },
    {
      label: 'Guest experience',
      body: themeReasoning.impact_fit || `${theme?.catalog_quick_workflow ? 'The plan' : 'The concept'} gives ${hasProvidedGuestCount ? `${eventData.guest_count}` : 'your'} guests a clear visual story, activities, and shopping list instead of a loose moodboard.`
    },
    {
      label: 'Sourcing strategy',
      body: themeReasoning.local_availability || `Items are designed to be shoppable or DIY-friendly around ${venueLabel}, with catalog matches and substitutions available where possible.`
    },
    {
      label: 'Safety and audience',
      body: `Suggestions are checked for a ${ageRange} audience and should still be reviewed against venue rules, dietary needs, and real-world availability.`
    }
  ];

  const addCatalogItemToPlan = (catalogItem) => {
    const candidateKey = catalogItem?.catalog_key || `${catalogItem?.catalog_type || 'item'}:${String(catalogItem?.catalog_id || catalogItem?.name || '').toLowerCase()}`;
    const companionItems = Array.isArray(catalogItem?.companion_items) ? catalogItem.companion_items : [];
    const bundleItems = [catalogItem, ...companionItems].filter(Boolean);
    const getLinkedActivities = (item) => {
      if (Array.isArray(item?.linked_activities) && item.linked_activities.length > 0) {
        return item.linked_activities
          .map((link) => ({
            key: String(link?.key || link?.name || '').trim(),
            name: String(link?.name || '').trim(),
            quantity: Number(link?.quantity || 1)
          }))
          .filter((link) => link.key || link.name);
      }

      if (item?.parent_activity_name || item?.parent_activity_key) {
        return [{
          key: String(item?.parent_activity_key || item?.parent_activity_name || '').trim(),
          name: String(item?.parent_activity_name || '').trim(),
          quantity: Number(item?.quantity || 1)
        }];
      }

      return [];
    };
    const calculateMergedSupplyQuantity = (linkedActivities = [], usageType = 'reusable') => {
      const quantities = linkedActivities
        .map(activity => Number(activity.quantity || 1))
        .filter(q => Number.isFinite(q) && q > 0);

      if (!quantities.length) return 1;

      if (usageType === 'consumable') {
        return quantities.reduce((sum, q) => sum + q, 0);
      }

      return Math.max(...quantities);
    };
    const mergeActivitySupplyItem = (existing, incoming) => {
      const existingLinks = getLinkedActivities(existing);
      const incomingLinks = getLinkedActivities(incoming);
      const linkMap = new Map();

      [...existingLinks, ...incomingLinks].forEach((link) => {
        const key = String(link.key || link.name || '').trim();
        if (!key) return;
        linkMap.set(key, {
          key,
          name: link.name,
          quantity: Number(link.quantity || 1)
        });
      });

      const linked_activities = Array.from(linkMap.values());
      const supply_usage_type = incoming.supply_usage_type || existing.supply_usage_type || 'reusable';
      const quantity = calculateMergedSupplyQuantity(linked_activities, supply_usage_type);
      const activityNames = linked_activities.map(link => link.name).filter(Boolean);

      return {
        ...existing,
        ...incoming,
        linked_activities,
        supply_usage_type,
        quantity,
        description: activityNames.length ? `Needed for ${activityNames.join(', ')}.` : incoming.description || existing.description,
        quantity_reason: activityNames.length ? `Needed for ${activityNames.join(', ')}` : incoming.quantity_reason || existing.quantity_reason,
        parent_activity_key: undefined,
        parent_activity_name: undefined,
        parent_activity_ref: undefined,
        parent_activity_id: undefined
      };
    };
    const detachActivityFromSupplyItem = (item, parent) => {
      const parentKeys = [
        parent?.catalog_key,
        parent?.catalog_ref,
        parent?.catalog_id,
        parent?.id,
        parent?._id,
        parent?.name
      ].filter(Boolean).map(String);
      const parentName = String(parent?.name || '');
      const supply_usage_type = item?.supply_usage_type || 'reusable';
      const linked_activities = getLinkedActivities(item).filter((link) => {
        const linkKey = String(link.key || '');
        const linkName = String(link.name || '');
        return !parentKeys.includes(linkKey) && !(parentName && linkName === parentName);
      });

      if (!linked_activities.length) return null;

      const activityNames = linked_activities.map(link => link.name).filter(Boolean);

      return {
        ...item,
        linked_activities,
        quantity: calculateMergedSupplyQuantity(linked_activities, supply_usage_type),
        description: activityNames.length ? `Needed for ${activityNames.join(', ')}.` : item.description,
        quantity_reason: activityNames.length ? `Needed for ${activityNames.join(', ')}` : item.quantity_reason,
        parent_activity_key: linked_activities.length === 1 ? linked_activities[0].key : undefined,
        parent_activity_name: linked_activities.length === 1 ? linked_activities[0].name : undefined,
        parent_activity_ref: undefined,
        parent_activity_id: undefined
      };
    };
    const mergeCatalogItemsIntoList = (prevItems, incomingItems) => {
      const nextItems = [...prevItems];

      incomingItems.forEach((incomingItem) => {
        if (!incomingItem) return;

        if (incomingItem.catalog_type === 'activity_supply') {
          const incomingSupplyKey = incomingItem.supply_key || incomingItem.catalog_key;
          const existingIndex = nextItems.findIndex((item) =>
            item?.catalog_type === 'activity_supply'
            && (item?.supply_key === incomingSupplyKey || item?.catalog_key === incomingSupplyKey)
          );

          if (existingIndex >= 0) {
            nextItems[existingIndex] = mergeActivitySupplyItem(nextItems[existingIndex], incomingItem);
          } else {
            nextItems.push(incomingItem);
          }
          return;
        }

        const alreadyExists = nextItems.some((item) => (
          (incomingItem?.catalog_key && item?.catalog_key === incomingItem.catalog_key)
          || (!incomingItem?.catalog_key && item?.id === incomingItem.id)
        ));

        if (!alreadyExists) {
          nextItems.push(incomingItem);
        }
      });

      return nextItems;
    };
    const isCompanionSupplyForActivity = (candidate, parent) => {
      const parentLinks = [
        candidateKey,
        parent?.catalog_key,
        parent?.catalog_ref,
        parent?.catalog_id
      ].filter(Boolean).map(String);
      const candidateParentKey = String(candidate?.parent_activity_key || '');
      const candidateParentRef = String(candidate?.parent_activity_ref || '');
      const candidateParentId = String(candidate?.parent_activity_id || '');
      const parentName = String(parent?.name || '');
      const candidateParentName = String(candidate?.parent_activity_name || '');
      const description = String(candidate?.description || '');
      const quantityReason = String(candidate?.quantity_reason || '');
      const linkedActivityKeys = getLinkedActivities(candidate)
        .flatMap((link) => [link.key, link.name])
        .filter(Boolean)
        .map(String);

      return (
        (candidate?.catalog_type === 'activity_supply' || candidateParentKey || candidateParentName)
        && (
          parentLinks.includes(candidateParentKey)
          || parentLinks.includes(candidateParentRef)
          || parentLinks.includes(candidateParentId)
          || linkedActivityKeys.some((key) => parentLinks.includes(key))
          || (parentName && candidateParentName === parentName)
          || (candidate?.catalog_type === 'activity_supply' && parentName && (description.includes(parentName) || quantityReason.includes(parentName)))
        )
      );
    };
    const shouldRemoveCatalogBundleItem = (item) => (
      (candidateKey && item?.catalog_key === candidateKey) ||
      (item?.name === catalogItem?.name && item?.category === catalogItem?.category) ||
      isCompanionSupplyForActivity(item, catalogItem)
    );

    const matchesCatalogItem = (item) =>
      (candidateKey && item.catalog_key === candidateKey) ||
      (item.name === catalogItem.name && item.category === catalogItem.category);

    const exists = items.some(item => matchesCatalogItem(item));

    if (exists) {
      setItems(prev => prev.map((item) => {
        if (isCompanionSupplyForActivity(item, catalogItem)) {
          return detachActivityFromSupplyItem(item, catalogItem);
        }
        return item;
      }).filter((item) => item && !shouldRemoveCatalogBundleItem(item)));
      return true;
    }

    setItems(prev => mergeCatalogItemsIntoList(prev, bundleItems));

    setExpandedCategories(prev => {
      const next = { ...prev };
      bundleItems.forEach((item) => {
        if (item?.category) next[item.category] = true;
      });
      return next;
    });

    return true;
  };

  const country = eventData?.country || 'US';
  const parseMoney = (value) => {
    if (typeof value === 'number' && Number.isFinite(value)) return value;
    if (!value) return 0;
    const match = String(value).replace(/,/g, '').match(/-?\d+(\.\d+)?/);
    return match ? Number(match[0]) : 0;
  };
  const formatMoney = useCallback((value, options = {}) => {
    const amount = Number(value || 0);
    const decimals = options.decimals ?? (Math.abs(amount % 1) > 0.001 ? 2 : 0);
    return formatCurrency(amount, country, { decimals });
  }, [country]);
  const hasPricingEvidence = (item) => {
    if (!item) return false;
    if (Array.isArray(item.shortlistedLinks) && item.shortlistedLinks.length > 0) return true;
    if (Array.isArray(item.links) && item.links.length > 0) return true;
    if (Array.isArray(item.sourcing_channels) && item.sourcing_channels.length > 0) return true;
    if (item.purchase_url || item.buy_url || item.source_url) return true;
    if (item.vendor || item.purchase_vendor || item.brand || item.estimate_source === 'User Added' || item.estimate_source === 'user') return true;
    if (item.origin === 'user' || item.is_custom === true || item.isCustom === true || item.userAdded === true) return true;
    
    const isCatalog = item.workflow_origin === 'catalog' || item.origin === 'catalog' || item.catalog_key || item.catalog_ref;
    const isAI = item.source === 'ai' || item.generated_by === 'ai' || item.is_ai_generated || item.origin === 'generated' || item.workflow_origin === 'generated';
    if (!isCatalog && !isAI) return true;

    return false;
  };

  const formatEstimatedMoney = useCallback((value, options = {}, item = null) => {
    let opts = typeof options === 'object' && options !== null && !options.hasOwnProperty('id') ? options : {};
    let itemObj = item;
    if (options && typeof options === 'object' && (options.hasOwnProperty('id') || options.hasOwnProperty('catalog_key') || options.hasOwnProperty('name'))) {
      itemObj = options;
    }

    const parsed = typeof value === 'number' ? value : parseMoney(value);
    if (value === null || value === undefined || isNaN(parsed) || parsed <= 0) {
      return 'Estimate needed';
    }
    if (parsed <= 1) {
      if (itemObj && !hasPricingEvidence(itemObj)) {
        return 'Estimate needed';
      }
    }
    return `Est. ${formatMoney(parsed, opts)}`;
  }, [formatMoney]);
  const sourceItems = Array.isArray(items) && items.length
    ? items
    : Object.entries(theme?.categories || {}).flatMap(([category, categoryItems]) => (
      Array.isArray(categoryItems)
        ? categoryItems.map((item) => ({ ...item, category }))
        : []
    ));
  const normalizedItems = sourceItems.map((item, idx) => {
    const buyPrice = parseMoney(item.buy_price ?? item.total_price ?? item.estimated_price ?? item.price);
    const rentPrice = parseMoney(item.rent_price ?? item.rent_total ?? item.rental_price);
    const optimizedPrice = item.rentable && rentPrice > 0 ? rentPrice : buyPrice;
    const normalizedCategory = item.category || item.category_name || 'Other';

    return {
      ...item,
      id: item.id || item._id || `${normalizedCategory}-${idx}`,
      category: normalizedCategory,
      buyPrice,
      rentPrice,
      optimizedPrice,
      priority: item.priority || item.importance || 'nice-to-have'
    };
  });
  const isMustHave = (item) => {
    const priority = String(item.priority || '').toLowerCase();
    return priority.includes('must') || priority.includes('essential') || item.required === true;
  };
  const matchesAny = (item, terms) => {
    const haystack = `${item.category || ''} ${item.name || ''} ${item.description || ''}`.toLowerCase();
    return terms.some((term) => haystack.includes(term));
  };
  const mustHaveItems = normalizedItems.filter(isMustHave);
  const optionalItems = normalizedItems.filter((item) => !isMustHave(item));
  const activityItems = normalizedItems.filter((item) => matchesAny(item, ['activity', 'game', 'craft', 'entertainment', 'favor']));
  const foodItems = normalizedItems.filter((item) => matchesAny(item, ['food', 'drink', 'menu', 'snack', 'beverage', 'cake', 'dessert']));
  const sortedFoodItems = useMemo(() => {
    const order = ['appetizer', 'main course', 'snack', 'dessert', 'drink', 'cake', 'other'];
    
    return [...foodItems].sort((a, b) => {
      const typeA = getMealType(a).toLowerCase();
      const typeB = getMealType(b).toLowerCase();
      
      const idxA = order.indexOf(typeA);
      const idxB = order.indexOf(typeB);
      
      if (idxA === -1 && idxB === -1) return typeA.localeCompare(typeB);
      if (idxA === -1) return 1;
      if (idxB === -1) return -1;
      return idxA - idxB;
    });
  }, [foodItems]);
  const computedOptimizedTotal = normalizedItems.reduce((sum, item) => sum + item.optimizedPrice, 0);
  const optimizedTotal = computedOptimizedTotal > 0
    ? computedOptimizedTotal
    : parseMoney(budgetGuardrail.optimized_total) || 0;
  const computedBuyTotal = normalizedItems.reduce((sum, item) => sum + (item.buyPrice || item.optimizedPrice), 0);
  const buyTotal = computedBuyTotal > 0
    ? computedBuyTotal
    : parseMoney(budgetGuardrail.buy_total) || 0;
  const budgetNumber = parseMoney(eventData?.budget || budgetGuardrail.budget);
  const mustHaveTotal = mustHaveItems.reduce((sum, item) => sum + item.optimizedPrice, 0);
  const optionalTotal = optionalItems.reduce((sum, item) => sum + item.optimizedPrice, 0);
  const rentSavings = Math.max(0, buyTotal - optimizedTotal);
  const budgetDelta = budgetNumber ? budgetNumber - optimizedTotal : 0;
  const budgetDeltaLabel = !budgetNumber
    ? 'Budget not set'
    : budgetDelta >= 0
      ? `Est. ${formatMoney(budgetDelta)} cushion`
      : `Est. ${formatMoney(Math.abs(budgetDelta))} over`;
  const budgetOverRatio = budgetNumber && budgetDelta < 0
    ? Math.abs(budgetDelta) / Math.max(budgetNumber, 1)
    : 0;
  const budgetToneTextClass = !budgetNumber
    ? 'text-gray-500'
    : budgetDelta >= 0
      ? 'text-emerald-700'
      : budgetOverRatio <= 0.03
        ? 'text-amber-700'
        : budgetOverRatio <= 0.1
          ? 'text-orange-700'
          : 'text-red-600';
  const budgetToneBgClass = !budgetNumber
    ? 'border-gray-200 bg-gray-50 text-gray-700'
    : budgetDelta >= 0
      ? 'border-emerald-200 bg-emerald-50 text-emerald-800'
      : budgetOverRatio <= 0.03
        ? 'border-amber-200 bg-amber-50 text-amber-800'
        : budgetOverRatio <= 0.1
          ? 'border-orange-200 bg-orange-50 text-orange-800'
          : 'border-red-200 bg-red-50 text-red-800';
  const categorySpend = Object.values(normalizedItems.reduce((acc, item) => {
    const key = item.category || 'Other';
    if (!acc[key]) {
      acc[key] = { name: key, count: 0, optimizedTotal: 0 };
    }
    acc[key].count += 1;
    acc[key].optimizedTotal += item.optimizedPrice;
    return acc;
  }, {})).sort((a, b) => b.optimizedTotal - a.optimizedTotal);
  const nextActions = [
    {
      title: 'Start with the essentials',
      body: mustHaveItems.length
        ? `Begin with the core items first: ${mustHaveItems.slice(0, 3).map((item) => item.name).join(', ')}.`
        : 'Begin with the core items first, then adjust extras based on your budget and space.',
      action: 'Open shopping',
      onClick: () => setActiveTab('shopping')
    },
    {
      title: 'Check sourcing',
      body: 'Use smart sourcing to compare practical buys, rentals, food vendors, DIY options, and substitutions before committing.',
      action: 'Open sourcing',
      onClick: () => setActiveTab('sourcing')
    },
    {
      title: 'Create digital invites & RSVP',
      body: 'Copy curated party invitation copy and collect RSVPs on Partiful or Paperless Post in seconds.',
      action: 'Send invites',
      onClick: () => setShowInviteModal(true)
    },
    {
      title: 'Trim or upgrade',
      body: budgetNumber
        ? `${budgetDeltaLabel}. Flexible extras account for ${formatEstimatedMoney(optionalTotal)}.`
        : `Flexible extras account for ${formatEstimatedMoney(optionalTotal)}.`,
      action: 'Review budget',
      onClick: () => setActiveTab('budget')
    }
  ];
  const fallbackTimeline = [
    {
      time: '2-3 weeks before',
      title: 'Confirm the event shape',
      tasks: ['Finalize guest count and venue constraints', 'Reserve rentals or high-risk items', 'Save catalog picks for price checks']
    },
    {
      time: '1 week before',
      title: 'Buy and prep',
      tasks: ['Order remaining decor and tableware', 'Prep printables, favors, and activity materials', 'Confirm menu quantities and dietary needs']
    },
    {
      time: 'Day before',
      title: 'Stage the setup',
      tasks: ['Group items by setup zone', 'Assemble DIY pieces', 'Pack a small repair and cleanup kit']
    },
    {
      time: 'Event day',
      title: 'Run the plan',
      tasks: ['Set up focal areas first', 'Check safety, walkways, food temperature, and weather backup', 'Keep optional upgrades for the final pass']
    }
  ];
	  const timelineBlocks = Array.isArray(theme?.timeline) && theme.timeline.length
	    ? theme.timeline.map((block, index) => {
	      if (typeof block === 'string') {
	        const time = `Step ${index + 1}`;
	        return { time, title: block, tasks: [], calendarDate: dateForOffset(inferTimelineOffset(time, index)) };
	      }
        const time = block.time || block.when || block.phase || `Step ${index + 1}`;
	      return {
	        time,
	        title: block.title || block.name || block.summary || 'Planning task',
	        tasks: block.tasks || block.items || block.checklist || [],
	        calendarDate: block.date || block.calendar_date || dateForOffset(inferTimelineOffset(time, index))
	      };
	    })
	    : (isDirectPlan ? [] : fallbackTimeline.map((block, index) => ({
        ...block,
        calendarDate: dateForOffset(inferTimelineOffset(block.time, index))
      })));
  const logisticsChecklist = [
    {
      title: 'Venue flow',
      body: `Map arrival, food, activities, and photo moments around ${venueLabel}. Keep walkways open.`
    },
    {
      title: 'Weather and backup',
      body: 'Confirm shade, rain backup, power access, and indoor alternatives before buying fragile decor.'
    },
    {
      title: 'Setup ownership',
      body: 'Assign setup, food, activity, and cleanup owners so the host is not carrying every task.'
    },
    {
      title: 'Safety review',
      body: `Review age fit for ${ageRange}, choking hazards, heat sources, alcohol handling, and venue rules.`
    }
  ];
  const planNavTabs = useMemo(() => {
    const focus = eventData?.focus_selection;
    const allowed = getAllowedPlanSections(focus);
    const primaryIntent = getPrimaryPlanningIntent(focus);
    const isThemeIdeas = primaryIntent === 'theme_ideas';

    const isActivities = primaryIntent === 'activities';

    const tabsList = [
      { id: 'overview', label: 'Overview', icon: '🧭' },
      { id: 'shopping', label: 'Shopping', icon: '🛒', cond: () => !isThemeIdeas && allowed.includes('shopping') && (!isDirectPlan || normalizedItems.length > 0) },
      { id: 'moodboard', label: 'Decor & Moodboard', icon: '🎨', cond: () => !isActivities },
      { id: 'timeline', label: 'Timeline', icon: '⏱️', cond: () => !isThemeIdeas && allowed.includes('timeline') && (!isDirectPlan || timelineBlocks.length > 0) },
      { id: 'budget', label: 'Budget', icon: '📊', cond: () => !isThemeIdeas && allowed.includes('budget') },
      { id: 'sourcing', label: 'Sourcing', icon: '🔍', cond: () => !isThemeIdeas && allowed.includes('sourcing') },
      { id: 'menu', label: 'Menu & Drinks', icon: '🍽️', cond: () => !isThemeIdeas && allowed.includes('menu') && (!isDirectPlan || foodItems.length > 0) },
      { id: 'activities', label: 'Activities', icon: '🎭', cond: () => !isThemeIdeas && allowed.includes('activities') && (!isDirectPlan || activityItems.length > 0) },
      { id: 'dress', label: 'Dress Code', icon: '👗', cond: () => !isThemeIdeas && allowed.includes('dress') && (!isDirectPlan || visibleCount > 0) },
      { id: 'logistics', label: 'Logistics', icon: '🚚', cond: () => !isThemeIdeas && allowed.includes('logistics') && primaryIntent !== 'food' }
    ];

    return tabsList.filter(tab => !tab.cond || tab.cond());
  }, [eventData?.focus_selection, theme, normalizedItems, activityItems, foodItems, isDirectPlan, timelineBlocks, visibleCount, displayedPalette]);

  const isTabAllowed = useCallback((tabId) => planNavTabs.some(t => t.id === tabId), [planNavTabs]);

  // Ensure active tab is allowed, otherwise fall back to intent default
  useEffect(() => {
    const allowedTabIds = planNavTabs.map((t) => t.id);
    if (activeTab === 'details' || !allowedTabIds.includes(activeTab)) {
      const focus = eventData?.focus_selection;
      const primaryIntent = getPrimaryPlanningIntent(focus);
      
      let fallback = 'overview';
      if (primaryIntent === 'food' && allowedTabIds.includes('menu')) {
        fallback = 'menu';
      } else if (primaryIntent === 'activities' && allowedTabIds.includes('activities')) {
        fallback = 'activities';
      } else if ((primaryIntent === 'decor' || primaryIntent === 'shopping') && allowedTabIds.includes('shopping')) {
        fallback = 'shopping';
      } else if (allowedTabIds.length > 0) {
        fallback = allowedTabIds[0];
      }
      
      setActiveTabState(fallback);
    }
  }, [planNavTabs, activeTab, eventData?.focus_selection]);

  // Mobile Plan consolidation states, callbacks, and responsive hooks
  const [windowWidth, setWindowWidth] = useState(typeof window !== 'undefined' ? window.innerWidth : 1024);
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const [activeMobileGroup, setActiveMobileGroup] = useState('command');
  const [isMobileTimelineOpen, setIsMobileTimelineOpen] = useState(false);
  const [isMobileActivitiesOpen, setIsMobileActivitiesOpen] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isMobileLogisticsOpen, setIsMobileLogisticsOpen] = useState(false);
  const [isMobileBudgetOpen, setIsMobileBudgetOpen] = useState(false);
  const [isMobileDetailView, setIsMobileDetailView] = useState(false);

  useEffect(() => {
    if (windowWidth < 1024) {
      if (activeTab === 'overview') {
        setIsMobileDetailView(false);
      } else {
        setIsMobileDetailView(true);
      }
    }
  }, [activeTab, windowWidth]);

  const isCommandGroupAllowed = isTabAllowed('overview') || isTabAllowed('timeline') || isTabAllowed('activities') || isTabAllowed('budget');
  const isSourcingGroupAllowed = isTabAllowed('shopping') || isTabAllowed('sourcing') || isTabAllowed('menu') || isTabAllowed('logistics');
  const isVibeGroupAllowed = isTabAllowed('colors') || isTabAllowed('dress');

  const mobileGroupTabs = useMemo(() => {
    const list = [];
    if (isCommandGroupAllowed) list.push({ id: 'command', label: 'Command', icon: '⚡' });
    if (isSourcingGroupAllowed) list.push({ id: 'sourcing_group', label: 'Sourcing', icon: '🛒' });
    if (isVibeGroupAllowed) list.push({ id: 'vibe', label: 'Vibe', icon: '🎨' });
    return list;
  }, [isCommandGroupAllowed, isSourcingGroupAllowed, isVibeGroupAllowed]);

  const handleMobileGroupChange = useCallback((groupId) => {
    setActiveMobileGroup(groupId);
    const primaryTabMap = {
      command: 'overview',
      sourcing_group: 'sourcing',
      vibe: 'colors'
    };
    const targetTab = primaryTabMap[groupId];
    let allowedTab = targetTab;
    if (!isTabAllowed(targetTab)) {
      if (groupId === 'command') {
        allowedTab = ['overview', 'timeline', 'activities', 'budget'].find(t => isTabAllowed(t)) || 'overview';
      } else if (groupId === 'sourcing_group') {
        allowedTab = ['sourcing', 'shopping', 'menu', 'logistics'].find(t => isTabAllowed(t)) || 'sourcing';
      } else if (groupId === 'vibe') {
        allowedTab = ['colors', 'dress'].find(t => isTabAllowed(t)) || 'colors';
      }
    }
    setActiveTab(allowedTab);
  }, [isTabAllowed, setActiveTab]);

  useEffect(() => {
    const isMobile = windowWidth < 1024;
    if (isMobile) {
      let parentGroup = 'command';
      if (['shopping', 'sourcing', 'menu', 'logistics'].includes(activeTab)) {
        parentGroup = 'sourcing_group';
      } else if (['colors', 'dress'].includes(activeTab)) {
        parentGroup = 'vibe';
      } else if (activeTab === 'budget') {
        parentGroup = 'command';
      }
      setActiveMobileGroup(parentGroup);
    } else {
      const groupToTabMap = {
        command: 'overview',
        sourcing_group: 'sourcing',
        vibe: 'colors'
      };
      if (['command', 'sourcing_group', 'vibe'].includes(activeTab)) {
        setActiveTab(groupToTabMap[activeTab]);
      }
    }
  }, [windowWidth, activeTab, setActiveTab]);

  const isPlanTabActive = (tabId) => {
    return activeTab === tabId;
  };
  const currentThemePlanKey = getThemePlanKey(theme);
  // Stable order for the plan versions strip: group by theme set, then sort
  // alphabetically by theme name, then by themePlanKey as a tiebreaker.
  // Previously this sorted by updated_at desc, which caused the active
  // version to jump to the front of the list every time it was clicked
  // ("musical chairs" behavior).
  const plannedThemeEntries = Object.entries(themePlans || {})
    .filter(([, snapshot]) => snapshot?.selected_theme)
    .sort(([keyA, a], [keyB, b]) => {
      const themeA = a?.selected_theme || {};
      const themeB = b?.selected_theme || {};
      const setA = String(themeA.theme_set_id || themeA.theme_set_label || '');
      const setB = String(themeB.theme_set_id || themeB.theme_set_label || '');
      if (setA !== setB) return setA.localeCompare(setB);
      const nameA = String(themeA.name || themeA.title || '').toLowerCase();
      const nameB = String(themeB.name || themeB.title || '').toLowerCase();
      if (nameA !== nameB) return nameA.localeCompare(nameB);
      return String(keyA).localeCompare(String(keyB));
    });
  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    const media = window.matchMedia('(min-width: 1024px)');
    const apply = () => setIsWideLayout(media.matches);
    apply();
    media.addEventListener('change', apply);
    return () => media.removeEventListener('change', apply);
  }, []);
  useEffect(() => {
    if (!isWideLayout || activeTab !== 'details') return;
    setActiveTab('overview');
  }, [isWideLayout, activeTab, setActiveTab]);

  useEffect(() => {
    if (typeof window === 'undefined' || plannedThemeEntries.length <= 1) return;
    window.requestAnimationFrame(() => {
      const strip = planVersionsStripRef.current;
      if (!strip || !currentThemePlanKey) return;
      const activeButton = strip.querySelector(`[data-plan-version="${currentThemePlanKey}"]`);
      activeButton?.scrollIntoView({
        behavior: 'smooth',
        inline: 'center',
        block: 'nearest'
      });
    });
  }, [currentThemePlanKey, plannedThemeEntries.length]);



  // Scroll the active tab button into view in the horizontal strip — but do NOT
  // reposition the page scroll. Repositioning caused the "jump" the user reported
  // when clicking Shopping / Sourcing / Timeline etc. Tab content renders in place;
  // the user scrolls down to read it naturally.
  useLayoutEffect(() => {
    if (typeof window === 'undefined') return undefined;
    const raf = window.requestAnimationFrame(() => {
      const strip = planTabStripRef.current;
      if (!strip) return;
      const activeButton = strip.querySelector(`[data-plan-tab="${activeTab}"]`);
      activeButton?.scrollIntoView({
        behavior: 'smooth',
        inline: 'nearest',
        block: 'nearest'
      });
    });
    return () => window.cancelAnimationFrame(raf);
  }, [activeTab]);

  const themeDisplayName = theme?.name || theme?.title || 'Selected theme';
  const eventDisplayName =
    eventData?.event_name ||
    eventData?.eventName ||
    eventType ||
    eventData?.event_type ||
    eventData?.eventType ||
    'Event';
  const intentConfig = getPlanningIntentConfig(eventData?.focus_selection);
  const fallbackTitle = eventDisplayName && eventDisplayName !== 'Event'
    ? `${eventDisplayName} ${intentConfig.label}`
    : intentConfig.outputTitle;
  const planDisplayName =
    theme?.plan_name ||
    theme?.planName ||
    theme?.plan_title ||
    theme?.planTitle ||
    eventData?.plan_name ||
    eventData?.planName ||
    fallbackTitle;

  const handleSharePlan = () => {
    if (typeof onShare === 'function') {
      onShare();
      return;
    }
    if (navigator?.share) {
      navigator.share({
        title: planDisplayName,
        text: theme?.description || ''
      }).catch(() => {});
    }
  };

  const handlePrintChecklist = () => {
    const escapeHtml = (value) =>
      String(value ?? '')
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#39;');

    const printableItems = normalizedItems.filter(Boolean);

    if (printableItems.length === 0) {
      alert("No checklist items found for this plan.");
      return;
    }

    const groupedItems = printableItems.reduce((acc, item) => {
      const category = item.category || 'Other';
      if (!acc[category]) acc[category] = [];
      acc[category].push(item);
      return acc;
    }, {});

    const checklistSections = Object.entries(groupedItems)
      .map(([category, items]) => {
        const itemsHtml = items
          .map((item) => {
            const estimate =
              item.optimizedPrice > 0
                ? formatEstimatedMoney(item.optimizedPrice, {}, item)
                : item.buyPrice > 0
                  ? formatEstimatedMoney(item.buyPrice, {}, item)
                  : null;
            const quantity = item.quantity || item.qty || item.serves || item.guest_count || '';
            const cleanEstimate = estimate ? (estimate.startsWith('Est. ') ? estimate.substring(5) : estimate) : null;
            const descriptor = [
              quantity ? `${escapeHtml(quantity)}` : null,
              cleanEstimate ? (cleanEstimate === 'Estimate needed' ? 'Estimate needed' : `Est. ${escapeHtml(cleanEstimate)}`) : null
            ]
              .filter(Boolean)
              .join(' • ');
            return `
              <li>
                <span class="checkbox"></span>
                <div class="item-copy">
                  <div class="item-name">${escapeHtml(item.name || item.title || 'Untitled item')}</div>
                  ${item.description ? `<div class="item-desc">${escapeHtml(item.description)}</div>` : ''}
                  ${descriptor ? `<div class="item-meta">${descriptor}</div>` : ''}
                </div>
              </li>
            `;
          })
          .join('');

        return `
          <section class="section">
            <h2>${escapeHtml(category)}</h2>
            <ul class="checklist">
              ${itemsHtml}
            </ul>
          </section>
        `;
      })
      .join('');

    const normalizeString = (str) =>
      String(str || '')
        .toLowerCase()
        .replace(/[^\w]/g, '')
        .trim();

    let timelineMarkup = '';
    if (timelineBlocks.length) {
      const itemsHtml = timelineBlocks
        .map((block, idx) => {
          const labelVal = block.time || '';
          const taskVal = block.title || block.task || block.description || '';
          const normLabel = normalizeString(labelVal);
          const normTask = normalizeString(taskVal);
          const showLabel = normLabel && normTask && normLabel !== normTask;

          let displayText = '';
          if (showLabel) {
            displayText = `${escapeHtml(labelVal)} &mdash; ${escapeHtml(taskVal)}`;
          } else {
            displayText = escapeHtml(taskVal || labelVal || 'Upcoming');
          }

          return `
            <li>
              <div class="timeline-task">
                <span class="timeline-number">${idx + 1}.</span>
                ${displayText}
              </div>
            </li>
          `;
        })
        .join('');

      timelineMarkup = `
        <section class="section">
          <h2>Timeline</h2>
          <ul class="timeline">
            ${itemsHtml}
          </ul>
        </section>
      `;
    }

    const printWindow = window.open('', '_blank', 'width=980,height=820');
    if (!printWindow) {
      alert("Please allow pop-ups to print the checklist.");
      return;
    }

    const summaryBits = [
      eventDisplayName ? `<span>${escapeHtml(eventDisplayName)}</span>` : '',
      guestCount ? `<span>${escapeHtml(guestCount)} guests</span>` : '',
      eventDate ? `<span>${escapeHtml(formatDateLabel(eventDate))}</span>` : '',
      venueLabel ? `<span>${escapeHtml(venueLabel)}</span>` : '',
      budgetSummary ? `<span>Budget ${escapeHtml(budgetSummary)}</span>` : ''
    ]
      .filter(Boolean)
      .join('<span class="dot">•</span>');

    const printHtml = `
      <!doctype html>
      <html lang="en">
        <head>
          <meta charset="utf-8" />
          <title>${escapeHtml(planDisplayName)} Checklist</title>
          <style>
            :root {
              color-scheme: light;
            }
            * {
              box-sizing: border-box;
            }
            body {
              margin: 0;
              padding: 80px 24px 40px 24px;
              font-family: Inter, ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif;
              color: #1f2937;
              background: #f9fafb;
              line-height: 1.4;
            }
            
            /* Preview toolbar style */
            .print-toolbar {
              position: fixed;
              top: 0;
              left: 0;
              right: 0;
              height: 56px;
              background: rgba(255, 255, 255, 0.95);
              backdrop-filter: blur(8px);
              border-bottom: 1px solid #e5e7eb;
              display: flex;
              align-items: center;
              justify-content: space-between;
              padding: 0 24px;
              z-index: 1000;
              box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
            }
            .toolbar-title {
              font-size: 14px;
              font-weight: 700;
              color: #111827;
              display: flex;
              align-items: center;
              gap: 8px;
            }
            .toolbar-actions {
              display: flex;
              align-items: center;
              gap: 12px;
            }
            .toolbar-helper {
              font-size: 11px;
              color: #6b7280;
            }
            .btn-primary {
              background: #7c3aed;
              color: #ffffff;
              border: none;
              padding: 8px 16px;
              font-size: 12px;
              font-weight: 600;
              border-radius: 6px;
              cursor: pointer;
              transition: all 0.15s ease-in-out;
              display: inline-flex;
              align-items: center;
              gap: 6px;
            }
            .btn-primary:hover {
              background: #6d28d9;
            }
            .btn-secondary {
              background: #ffffff;
              color: #374151;
              border: 1px solid #d1d5db;
              padding: 8px 16px;
              font-size: 12px;
              font-weight: 600;
              border-radius: 6px;
              cursor: pointer;
              transition: all 0.15s ease-in-out;
            }
            .btn-secondary:hover {
              background: #f9fafb;
              border-color: #c0c4cc;
            }

            .content-wrapper {
              background: #ffffff;
              max-width: 800px;
              margin: 0 auto;
              padding: 32px;
              box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.05), 0 2px 4px -1px rgba(0, 0, 0, 0.03);
              border: 1px solid #e5e7eb;
              border-radius: 8px;
            }

            .header {
              margin-bottom: 16px;
              padding-bottom: 12px;
              border-bottom: 1px solid #e5e7eb;
            }
            .eyebrow {
              margin: 0 0 3px;
              font-size: 9px;
              font-weight: 700;
              letter-spacing: 0.1em;
              text-transform: uppercase;
              color: #7c3aed;
            }
            .title {
              margin: 0;
              font-size: 20px;
              line-height: 1.2;
              font-weight: 700;
              color: #111827;
            }
            .subtitle {
              margin: 4px 0 0;
              font-size: 13px;
              color: #374151;
            }
            .summary {
              margin-top: 8px;
              display: flex;
              flex-wrap: wrap;
              gap: 8px;
              align-items: center;
              font-size: 11px;
              color: #4b5563;
            }
            .dot {
              color: #9ca3af;
              margin: 0 2px;
            }
            .section {
              margin-top: 16px;
              break-inside: avoid;
            }
            .section h2 {
              margin: 0 0 6px;
              font-size: 11px;
              font-weight: 700;
              color: #4b5563;
              text-transform: uppercase;
              letter-spacing: 0.05em;
              border-bottom: 1px solid #e5e7eb;
              padding-bottom: 3px;
            }
            .checklist,
            .timeline {
              list-style: none;
              margin: 0;
              padding: 0;
            }
            .checklist li,
            .timeline li {
              display: flex;
              gap: 8px;
              padding: 4px 0;
              border-bottom: 1px solid #e5e7eb;
              align-items: flex-start;
            }
            .checkbox {
              width: 12px;
              height: 12px;
              margin-top: 2.5px;
              border: 1px solid #4b5563;
              border-radius: 2px;
              flex: 0 0 12px;
            }
            .item-copy,
            .timeline li > div {
              min-width: 0;
            }
            .item-name,
            .timeline-task {
              font-size: 12px;
              font-weight: 600;
              color: #1f2937;
              line-height: 1.4;
            }
            .item-desc,
            .item-meta {
              margin-top: 2px;
              font-size: 10px;
              line-height: 1.3;
              color: #4b5563;
            }
            .timeline-number {
              display: inline-block;
              min-width: 16px;
              font-weight: 700;
              color: #4b5563;
              margin-right: 4px;
            }

            @media print {
              body {
                padding: 10px !important;
                background: #ffffff !important;
              }
              .print-toolbar {
                display: none !important;
              }
              .content-wrapper {
                box-shadow: none !important;
                border: none !important;
                padding: 0 !important;
                margin: 0 !important;
                max-width: 100% !important;
              }
            }
          </style>
        </head>
        <body>
          <div class="print-toolbar">
            <div class="toolbar-title">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" style="color: #7c3aed;"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path><polyline points="22 4 12 14.01 9 11.01"></polyline></svg>
              <span>Checklist Preview</span>
            </div>
            <div class="toolbar-actions">
              <span class="toolbar-helper">Use your browser’s print dialog to print or save as PDF.</span>
              <button class="btn-primary" onclick="window.print()">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="6 9 6 2 18 2 18 9"></polyline><path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2"></path><rect x="6" y="14" width="12" height="8"></rect></svg>
                Print / Save as PDF
              </button>
              <button class="btn-secondary" onclick="window.close()">Close</button>
            </div>
          </div>
          <div class="content-wrapper">
            <header class="header">
              <p class="eyebrow">CraftMyParty checklist</p>
              <h1 class="title">${escapeHtml(planDisplayName)}</h1>
              <p class="subtitle">${escapeHtml(themeDisplayName)}</p>
              <div class="summary">${summaryBits}</div>
            </header>
            ${checklistSections || '<p>No checklist items available yet.</p>'}
            ${timelineMarkup}
          </div>
        </body>
      </html>
    `;

    try {
      printWindow.document.open();
      printWindow.document.write(printHtml);
      printWindow.document.close();
    } catch (err) {
      console.error("Failed to write to print window:", err);
      alert("Failed to write print data. Please verify browser permissions.");
      return;
    }

    printWindow.focus();
  };

  const shareStatusLabel = isSharedView
    ? 'Shared with you'
    : sharePermissions
      ? 'Shared plan'
      : 'Private plan';
  const planNeedsReEstimate = needsPlanReEstimate(eventData);
  const planQuickFacts = useMemo(() => {
    const facts = [
      { label: 'Plan', value: planDisplayName },
      { label: 'Event', value: eventTypeDisplay },
      { label: 'Guests', value: hasProvidedGuestCount ? `${eventData.guest_count}` : 'Guest count not set' },
      { label: 'Date', value: eventDate ? formatDateLabel(eventDate) : 'Not set' }
    ];
    if (!isThemeIdeas) {
      facts.push({ label: 'Budget', value: budgetSummary });
      facts.push({ label: 'Est. spend', value: formatEstimatedMoney(optimizedTotal) });
    }
    facts.push({ label: 'Access', value: shareStatusLabel });
    return facts;
  }, [planDisplayName, eventTypeDisplay, hasProvidedGuestCount, eventData?.guest_count, eventDate, budgetSummary, optimizedTotal, shareStatusLabel, formatEstimatedMoney, isThemeIdeas]);

  const quickFactsCellMinWidth = useMemo(
    () => estimateQuickFactCellMinWidth(planQuickFacts),
    [planQuickFacts]
  );
  const shoppingListSubLabel = itemCount === 1 ? '1 item' : `${itemCount} items`;
  const planMetricsCellMinWidth = useMemo(
    () => estimatePlanMetricCellMinWidth({
      budget: budgetSummary,
      budgetSub: budgetStatusLabel,
      shopping: String(itemCount),
      shoppingSub: shoppingListSubLabel,
      catalog: 'Sourcing',
      catalogSub: 'View picks'
    }),
    [budgetSummary, budgetStatusLabel, itemCount, shoppingListSubLabel]
  );
  const quickFactsColumns = useMetricGridColumns(
    quickFactsGridRef,
    planQuickFacts.length,
    quickFactsCellMinWidth,
    2,
    { preferredOrder: QUICK_FACTS_COL_PREFERENCE, verifyNoWrap: true }
  );
  const planMetricsColumns = useMetricGridColumns(
    planMetricsGridRef,
    3,
    planMetricsCellMinWidth,
    1
  );

  const planMetricLabelClass = 'text-[9px] font-bold uppercase tracking-wide text-gray-500 leading-none sm:text-[10px]';
  const planMetricValueClass = 'mt-0.5 min-w-0 break-words text-[11px] font-extrabold leading-snug text-gray-950 sm:text-xs';
  const planMetricSubClass = 'mt-0.5 whitespace-normal break-words text-[9px] font-semibold leading-tight text-gray-500 sm:text-[10px]';
  const planMetricTileClass = 'min-h-0 min-w-0 rounded-lg border px-2 py-1.5 sm:px-2.5 sm:py-2';
  const sharePlanIcon = (
    <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z"
      />
    </svg>
  );
  const editPlanDetailsIcon = (
    <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
      />
    </svg>
  );

  if (isIncompletePlan) {
    return (
      <div className="mx-auto max-w-5xl animate-slide-up pt-10 sm:pt-14 md:pt-16 px-4 sm:px-6 lg:px-8 relative bg-gray-50 min-h-[70vh] flex items-center justify-center" style={{ zIndex: 10, position: 'relative' }}>
        <div className="w-full max-w-md bg-white border border-gray-200 rounded-3xl p-6 sm:p-8 shadow-lg text-center">
          <div className="inline-flex h-16 w-16 items-center justify-center rounded-2xl bg-amber-50 text-amber-500 mb-6 border border-amber-100">
            <span className="text-3xl" role="img" aria-label="Hourglass">⏳</span>
          </div>
          <h1 className="text-2xl font-black text-gray-900 mb-3">This plan was not fully generated</h1>
          <p className="text-sm text-gray-600 leading-relaxed mb-6">
            You started planning this {eventData?.event_type || 'event'} but did not complete the generation wizard. Resume now to get your custom timeline, budget, shopping list, and themes!
          </p>
          <button
            type="button"
            onClick={onResumePlanning}
            className="w-full py-3 px-4 bg-zinc-900 text-white rounded-xl font-bold hover:bg-zinc-700 transition-all shadow-md hover:shadow-lg focus:outline-none flex items-center justify-center gap-2"
          >
            <span>Resume Event Wizard</span>
            <span className="text-lg">→</span>
          </button>
        </div>
      </div>
    );
  }

	  return (
	    <div className="mx-auto max-w-5xl animate-slide-up pt-10 sm:pt-14 md:pt-16 relative bg-gray-50" style={{ zIndex: 10, position: 'relative' }}>
      {/* Plan versions strip — placed above the plan brief so switching between
          versions is a top-level navigation, not buried inside the brief card. */}
      {plannedThemeEntries.length > 1 && !theme?.catalog_quick_workflow && (
        <div className="mb-4 rounded-2xl border border-stone-200 bg-stone-50/80 p-3 shadow-sm">
          <p className="mb-2 text-[11px] font-bold uppercase tracking-[0.18em] text-stone-500">
            Plan versions
          </p>
          <div
            ref={planVersionsStripRef}
            className="flex gap-2 overflow-x-auto scroll-smooth pb-0.5 [scroll-padding-inline:12px] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
            role="list"
            aria-label="Plan theme versions"
          >
            {plannedThemeEntries.map(([key, snapshot]) => {
              const isCurrentPlan = key === currentThemePlanKey;
              const planTheme = snapshot.selected_theme;
              const planPalette = normalizePalette(planTheme?.color_palette).slice(0, 3);
              return (
                <button
                  key={key}
                  type="button"
                  data-plan-version={key}
                  disabled={isCurrentPlan}
                  onClick={() => onViewThemePlan?.(planTheme)}
                  className={`inline-flex min-h-9 shrink-0 items-center gap-2 rounded-full border px-3 py-1.5 text-xs font-bold transition ${
                    isCurrentPlan
                      ? 'border-zinc-900 bg-zinc-900 text-white shadow-sm'
                      : 'border-gray-200 bg-white text-gray-700 hover:border-stone-400 hover:bg-white'
                  }`}
                  aria-current={isCurrentPlan ? 'true' : undefined}
                >
                  <span className="flex -space-x-1">
                    {planPalette.map((color, idx) => (
                      <span
                        key={`${color.hex}-${idx}`}
                        className="h-3.5 w-3.5 rounded-full border border-gray-200 shadow-sm"
                        style={{ backgroundColor: color.hex }}
                      />
                    ))}
                  </span>
                  <span className="block whitespace-normal break-words">{planTheme?.name || 'Theme plan'}</span>
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* Mobile Plan Brief (lg:hidden) */}
      <section className="mb-6 overflow-hidden rounded-3xl border border-gray-200 bg-white shadow-sm block lg:hidden">
        <div className="p-4 sm:p-5">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div className="flex flex-wrap items-center gap-1.5">
              <span className="rounded-full bg-emerald-50 px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-[0.12em] text-emerald-700">
                Plan ready
              </span>
              <span className="rounded-full bg-stone-100 px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-[0.12em] text-stone-700">
                {intentConfig.dashboardBadge || intentConfig.label}
              </span>
              <span className="rounded-full bg-gray-100 px-2.5 py-0.5 text-[10px] font-semibold text-gray-600">
                {shareStatusLabel}
              </span>
            </div>
          </div>

          <div className="mt-3 flex items-start gap-2.5">
            <h1 className="flex-1 text-xl font-extrabold leading-tight text-gray-950 sm:text-2xl">
              {planDisplayName}
            </h1>
            {onToggleFavorite && (
              <button
                type="button"
                onClick={onToggleFavorite}
                className={`inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-full border transition ${
                  isFavorite
                    ? 'border-amber-300 bg-amber-50 text-amber-500'
                    : 'border-gray-200 bg-white text-gray-400'
                }`}
                title={isFavorite ? 'Remove from favorites' : 'Mark plan as favorite'}
                aria-label={isFavorite ? 'Remove from favorites' : 'Mark plan as favorite'}
                aria-pressed={Boolean(isFavorite)}
              >
                <svg className="h-4.5 w-4.5" fill={isFavorite ? 'currentColor' : 'none'} stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.196-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z" />
                </svg>
              </button>
            )}
          </div>

          {themeDisplayName && themeDisplayName !== planDisplayName && !theme?.catalog_quick_workflow && !isDirectPlan && (
            <p className="mt-1 text-xs font-bold text-stone-600">
              Inspired by: {themeDisplayName}
            </p>
          )}

          <p className="mt-2 text-xs leading-relaxed text-gray-600 sm:text-sm">
            {theme?.description}
          </p>

          {/* Horizontal scrollable metadata chips */}
          <div className="mt-4 flex gap-1.5 overflow-x-auto pb-1 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden scroll-smooth -mx-4 px-4 sm:-mx-5 sm:px-5">
            {eventDate && (
              <span className="inline-flex shrink-0 items-center gap-1 rounded-full border border-gray-200 bg-gray-50 px-2.5 py-1 text-[11px] font-semibold text-gray-705">
                📅 {formatDateLabel(eventDate)}
              </span>
            )}
            <span className="inline-flex shrink-0 items-center gap-1 rounded-full border border-gray-200 bg-gray-50 px-2.5 py-1 text-[11px] font-semibold text-gray-705">
              👥 {hasProvidedGuestCount ? `${eventData.guest_count} guests` : 'Guest count not set'}
            </span>
            {!isThemeIdeas && (
              <span className="inline-flex shrink-0 items-center gap-1 rounded-full border border-gray-200 bg-gray-50 px-2.5 py-1 text-[11px] font-semibold text-gray-705">
                💰 Budget: {budgetSummary}
              </span>
            )}
            {eventData?.venue_type && (
              <span className="inline-flex shrink-0 items-center gap-1 rounded-full border border-gray-200 bg-gray-50 px-2.5 py-1 text-[11px] font-semibold text-gray-705">
                📍 {eventData.venue_type}
              </span>
            )}
            {eventCountdown && (
              <span className="inline-flex shrink-0 items-center gap-1 rounded-full border border-gray-200 bg-gray-50 px-2.5 py-1 text-[11px] font-semibold text-gray-705">
                ⏳ {eventCountdown}
              </span>
            )}
            {!isThemeIdeas && itemCount > 0 && (
              <span className="inline-flex shrink-0 items-center gap-1 rounded-full border border-stone-200 bg-stone-100 px-2.5 py-1 text-[11px] font-semibold text-stone-700">
                🛒 {shoppedCount}/{itemCount} Shopped
              </span>
            )}
            {!isThemeIdeas && moneySpent > 0 && (
              <span className="inline-flex shrink-0 items-center gap-1 rounded-full border border-emerald-200 bg-emerald-50 px-2.5 py-1 text-[11px] font-semibold text-emerald-800">
                💵 Spent {formatCurrency(moneySpent, eventData?.country || 'US')}
              </span>
            )}
          </div>

          {/* Grouped Actions Toolbar */}
          <div className="mt-4 flex flex-wrap items-center gap-2">
            {onSavePlan && savePlanEnabled && (
              <button
                type="button"
                onClick={onSavePlan}
                className="ds-btn-primary flex-1 min-w-[95px] h-9 px-3 text-xs font-bold whitespace-nowrap rounded-xl"
              >
                Save Plan
              </button>
            )}
            <button
              type="button"
              onClick={handleSharePlan}
              className="inline-flex flex-1 min-w-[75px] h-9 items-center justify-center gap-1 rounded-xl border border-stone-200 bg-stone-50 px-3 text-xs font-bold text-stone-700 transition hover:bg-stone-100"
            >
              {sharePlanIcon}
              <span>{quickShareUrl ? 'Sharing' : 'Share'}</span>
            </button>
            {(onSaveCoreEventDetails || onReEstimatePlan) && (
              <button
                type="button"
                onClick={() => setShowEventDetailsModal(true)}
                className={`inline-flex h-9 w-9 items-center justify-center rounded-xl border bg-white text-gray-700 transition hover:bg-gray-50 shrink-0 ${
                  planNeedsReEstimate ? 'border-amber-300' : 'border-gray-200'
                }`}
                aria-label="Edit details"
                title="Edit guests, budget, or date"
              >
                {editPlanDetailsIcon}
              </button>
            )}
            <button
              type="button"
              onClick={() => setShowReasoningModal(true)}
              className="inline-flex h-9 items-center justify-center rounded-xl border border-amber-250 bg-amber-50 px-3 text-xs font-bold text-amber-800 transition hover:bg-amber-100 flex-1 min-w-[70px]"
            >
              Rationale
            </button>
            {typeof onBackToThemes === 'function' && isConceptFirst && !theme?.catalog_quick_workflow && (
              <button
                type="button"
                onClick={onBackToThemes}
                className="inline-flex h-9 items-center justify-center rounded-xl border border-gray-200 bg-white px-3 text-xs font-bold text-gray-700 transition hover:bg-gray-50 flex-1 min-w-[100px]"
              >
                Change Concept
              </button>
            )}
          </div>

          {!isThemeIdeas && planNeedsReEstimate && onReEstimatePlan && (
            <button
              type="button"
              onClick={() => onReEstimatePlan({
                guest_count: eventData?.guest_count,
                budget: eventData?.budget,
                date: eventData?.date || eventData?.event_date || null,
                event_date: eventData?.date || eventData?.event_date || null
              })}
              className="mt-3 h-9 w-full flex items-center justify-center rounded-xl bg-zinc-900 text-xs font-bold text-white shadow-sm transition hover:bg-zinc-700"
            >
              Re-estimate Plan
            </button>
          )}

          {/* Metrics Tiles */}
          {!isThemeIdeas && (
            <div className="mt-4 grid grid-cols-3 gap-2">
              <div className={`${planMetricTileClass} border-gray-200 bg-gray-50`}>
                <p className={planMetricLabelClass}>Budget</p>
                <p className={planMetricValueClass}>{budgetSummary}</p>
                <p className={planMetricSubClass}>{budgetStatusLabel}</p>
              </div>
              <button
                type="button"
                onClick={() => {
                  setActiveTab('shopping');
                  requestAnimationFrame(() => {
                    const panel = document.getElementById('plan-tab-panel-shopping');
                    if (panel) {
                      panel.scrollIntoView({ behavior: 'smooth', block: 'start' });
                    }
                  });
                }}
                className={`${planMetricTileClass} border-gray-200 bg-gray-50 text-left transition hover:border-stone-300 hover:bg-stone-50`}
              >
                <p className={planMetricLabelClass}>Shopping</p>
                <p className={planMetricValueClass}>{itemCount}</p>
                <p className={planMetricSubClass}>{shoppingListSubLabel}</p>
              </button>
              <button
                type="button"
                onClick={() => openCatalogExplorer({ tab: 'items' })}
                className={`${planMetricTileClass} border-stone-200 bg-stone-50 text-left transition hover:border-stone-300 hover:bg-white`}
              >
                <p className={`${planMetricLabelClass} text-stone-600`}>Catalog</p>
                <p className={planMetricValueClass}>Sourcing</p>
                <p className={`${planMetricSubClass} text-stone-600`}>View picks</p>
              </button>
            </div>
          )}

          {perGuestSummary && (
            <p className="mt-3 text-center text-[10px] font-semibold text-gray-500">
              Approx. {perGuestSummary.replace('Approx. ', '')} per guest
            </p>
          )}

          <div className="mt-3.5 border-t border-gray-100 pt-2 text-[10px] leading-relaxed text-gray-500 text-center">
            AI plans are a planning aid. Verify details, safety, and venue rules.
          </div>
        </div>
      </section>

      {/* Desktop Plan Brief */}
      <section className="mb-6 overflow-hidden rounded-3xl border border-gray-200 bg-white shadow-sm hidden lg:block">
        <div className="grid min-w-0 gap-5 p-5 sm:p-6 lg:grid-cols-[minmax(0,1fr)_minmax(0,300px)] lg:items-start lg:gap-6 lg:p-8">
          <div className="min-w-0">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div className="flex flex-wrap items-center gap-2">
              <span className="rounded-full bg-emerald-50 px-3 py-1 text-xs font-bold uppercase tracking-[0.18em] text-emerald-700">
                Plan ready
              </span>
              <span className="rounded-full bg-stone-100 px-3 py-1 text-xs font-bold uppercase tracking-[0.18em] text-stone-700">
                {intentConfig.dashboardBadge || intentConfig.label}
              </span>
              <span className="rounded-full bg-gray-100 px-3 py-1 text-xs font-semibold text-gray-600">
                {shareStatusLabel}
              </span>
            </div>
            <div className="flex items-center gap-2">
              {typeof onBackToThemes === 'function' && isConceptFirst && !theme?.catalog_quick_workflow && (
                <button
                  type="button"
                  onClick={onBackToThemes}
                  className="ds-btn-ghost shrink-0 px-4 py-2 text-sm"
                >
                  Change Concept
                </button>
              )}
              {onSavePlan && savePlanEnabled && (
                <button
                  type="button"
                  onClick={onSavePlan}
                  className="ds-btn-primary shrink-0 px-4 py-2 text-sm"
                >
                  Save plan
                </button>
              )}
            </div>
          </div>

          {/* min-h prevents the brief card from shifting up/down when the theme
              name length changes during plan-version switches. */}
          <div className="mt-4 flex items-start gap-3">
            <h1 className="min-h-[3rem] sm:min-h-[3.5rem] lg:min-h-[4rem] flex-1 text-2xl font-extrabold leading-tight text-gray-950 sm:text-4xl lg:text-5xl">
              {planDisplayName}
            </h1>
            {onToggleFavorite && (
              <button
                type="button"
                onClick={onToggleFavorite}
                className={`mt-1 inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-full border transition ${
                  isFavorite
                    ? 'border-amber-300 bg-amber-50 text-amber-500'
                    : 'border-gray-200 bg-white text-gray-400 hover:border-amber-300 hover:text-amber-400'
                }`}
                title={isFavorite ? 'Remove from favorites' : 'Mark plan as favorite'}
                aria-label={isFavorite ? 'Remove from favorites' : 'Mark plan as favorite'}
                aria-pressed={Boolean(isFavorite)}
              >
                <svg className="h-5 w-5" fill={isFavorite ? 'currentColor' : 'none'} stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.196-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z" />
                </svg>
              </button>
            )}
          </div>
          <p className="mt-3 min-h-[3.5rem] text-sm leading-relaxed text-gray-600 sm:text-base lg:text-lg">
            {theme?.description}
          </p>
          {themeDisplayName && themeDisplayName !== planDisplayName && !theme?.catalog_quick_workflow && !isDirectPlan && (
            <div className="mt-3 inline-flex max-w-full items-center rounded-full border border-stone-200 bg-stone-100 px-3 py-1 text-xs font-semibold text-stone-700">
              Based on {getConceptLabelForIntent(eventData?.focus_selection, 'concept-lower')}: <span className="ml-1 whitespace-normal break-words">{themeDisplayName}</span>
            </div>
          )}

          {((!isThemeIdeas && (itemCount > 0 || moneySpent > 0)) || eventCountdown) && (
            <div className="mt-3 flex flex-wrap gap-2">
              {!isThemeIdeas && itemCount > 0 && (
                <span className="inline-flex items-center rounded-full border border-stone-200 bg-stone-100 px-3 py-1 text-xs font-semibold text-stone-700">
                  Shopped {shoppedCount}/{itemCount}
                </span>
              )}
              {eventCountdown && (
                <span className="inline-flex items-center rounded-full border border-gray-200 bg-gray-50 px-3 py-1 text-xs font-semibold text-gray-700">
                  {eventCountdown}
                </span>
              )}
              {!isThemeIdeas && moneySpent > 0 && (
                <span className="inline-flex items-center rounded-full border border-emerald-200 bg-emerald-50 px-3 py-1 text-xs font-semibold text-emerald-800">
                  Spent {formatCurrency(moneySpent, eventData?.country || 'US')}
                  {eventData?.budget ? ` / ${budgetSummary}` : ''}
                </span>
              )}
            </div>
          )}

          {!isThemeIdeas && (
            <div
              ref={planMetricsGridRef}
              className="mt-5 grid gap-1.5"
              style={{ gridTemplateColumns: `repeat(${planMetricsColumns}, minmax(0, 1fr))` }}
            >
              <div className={`${planMetricTileClass} border-gray-200 bg-gray-50`}>
                <p className={planMetricLabelClass}>Budget</p>
                <p className={planMetricValueClass}>{budgetSummary}</p>
                <p className={planMetricSubClass}>{budgetStatusLabel}</p>
              </div>
              <button
                type="button"
                onClick={() => {
                  // Always scroll to the shopping panel, even if already on shopping tab
                  setActiveTab('shopping');
                  requestAnimationFrame(() => {
                    const panel = document.getElementById('plan-tab-panel-shopping');
                    if (panel) {
                      panel.scrollIntoView({ behavior: 'smooth', block: 'start' });
                    }
                  });
                }}
                className={`${planMetricTileClass} border-gray-200 bg-gray-50 text-left transition hover:border-stone-300 hover:bg-stone-50`}
              >
                <p className={planMetricLabelClass}>Shopping list</p>
                <p className={planMetricValueClass}>{itemCount}</p>
                <p className={planMetricSubClass}>{shoppingListSubLabel}</p>
              </button>
              <button
                type="button"
                onClick={() => openCatalogExplorer({ tab: 'items' })}
                className={`${planMetricTileClass} border-stone-200 bg-stone-50 text-left transition hover:border-stone-300 hover:bg-white`}
              >
                <p className={`${planMetricLabelClass} text-stone-600`}>Catalog</p>
                <p className={planMetricValueClass}>Sourcing</p>
                <p className={`${planMetricSubClass} text-stone-600`}>View picks</p>
              </button>
            </div>
          )}

          </div>

          <aside className="min-w-0 rounded-2xl border border-gray-200 bg-gray-50 p-3 sm:p-3.5 lg:sticky lg:top-24">
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0">
                <p className="text-[10px] font-bold uppercase tracking-[0.16em] text-gray-500">Quick facts</p>
                <h2 className="mt-0.5 text-base font-extrabold text-gray-950 sm:text-lg">Plan snapshot</h2>
              </div>
              <div className="flex shrink-0 items-center gap-1.5">
                {(onSaveCoreEventDetails || onReEstimatePlan) && (
                  <button
                    type="button"
                    onClick={() => setShowEventDetailsModal(true)}
                    className={`relative inline-flex h-8 w-8 items-center justify-center rounded-lg border bg-white text-zinc-700 transition hover:bg-stone-50 ${
                      planNeedsReEstimate
                        ? 'border-amber-300 hover:border-amber-400'
                        : 'border-stone-200 hover:border-stone-300'
                    }`}
                    aria-label={planNeedsReEstimate ? 'Edit plan details — re-estimate pending' : 'Edit guests, budget, or date'}
                    title={planNeedsReEstimate ? 'Details changed — open to re-estimate' : 'Edit guests, budget, or date'}
                  >
                    {editPlanDetailsIcon}
                    {planNeedsReEstimate && (
                      <span className="absolute -right-0.5 -top-0.5 h-2 w-2 rounded-full bg-amber-500 ring-2 ring-white" aria-hidden="true" />
                    )}
                  </button>
                )}
                <button
                  type="button"
                  onClick={() => setShowReasoningModal(true)}
                  className="inline-flex h-8 shrink-0 items-center rounded-lg border border-amber-200 bg-amber-50 px-2.5 text-[11px] font-bold text-amber-800 transition hover:bg-amber-100"
                >
                  Rationale
                </button>
              </div>
            </div>

            <div
              ref={quickFactsGridRef}
              className="mt-3 grid gap-1.5"
              style={{ gridTemplateColumns: `repeat(${quickFactsColumns}, minmax(0, 1fr))` }}
            >
              {planQuickFacts.map((fact) => (
                <div key={fact.label} className={`${planMetricTileClass} border-gray-200 bg-white`}>
                  <p className={`${planMetricLabelClass} text-gray-400`}>{fact.label}</p>
                  <p className={planMetricValueClass} data-metric-value title={String(fact.value)}>{fact.value}</p>
                </div>
              ))}
            </div>

            {!isThemeIdeas && perGuestSummary && (
              <p className="mt-2 rounded-lg bg-white px-2 py-1 text-[10px] font-semibold leading-snug text-gray-600 sm:text-[11px]">
                Approx. {perGuestSummary} per guest
              </p>
            )}

            {!isThemeIdeas && planNeedsReEstimate && onReEstimatePlan && (
              <button
                type="button"
                onClick={() => onReEstimatePlan({
                  guest_count: eventData?.guest_count,
                  budget: eventData?.budget,
                  date: eventData?.date || eventData?.event_date || null,
                  event_date: eventData?.date || eventData?.event_date || null
                })}
                className="mt-2 flex h-10 w-full items-center justify-center rounded-xl bg-zinc-900 text-xs font-bold text-white shadow-sm transition hover:bg-zinc-700"
              >
                Re-estimate plan
              </button>
            )}

            <div className="mt-2 rounded-lg border border-gray-200 bg-white px-2.5 py-2 sm:px-3 sm:py-2.5">
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0 flex-1">
                  <p className="text-[10px] font-bold uppercase tracking-wider text-gray-400">Sharing</p>
                  <p className="mt-0.5 text-xs font-extrabold text-gray-950">
                    {quickShareUrl ? 'Quick link ready' : shareLookupDone ? 'Private until shared' : 'Checking...'}
                  </p>
                  <p className="mt-1 break-words text-[11px] leading-relaxed text-gray-500">
                    {quickShareUrl
                      ? 'Anyone with this link can view based on the permissions you chose.'
                      : 'Only you can access this plan until you create a link or invite someone by email.'}
                  </p>
                </div>
                <div className="flex shrink-0 flex-col gap-1.5 sm:flex-row">
                  {quickShareUrl && (
                    <button
                      type="button"
                      onClick={() => navigator.clipboard?.writeText(quickShareUrl)}
                      className="inline-flex h-8 w-8 items-center justify-center rounded-full border border-gray-200 text-gray-600 transition hover:bg-gray-50"
                      title="Copy share link"
                    >
                      <svg className="h-3.5 w-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                      </svg>
                    </button>
                  )}
                  <button
                    type="button"
                    onClick={handleSharePlan}
                    className="inline-flex h-8 w-8 items-center justify-center rounded-full border border-stone-200 bg-stone-50 text-stone-700 transition hover:bg-stone-100"
                    title={quickShareUrl ? 'Manage sharing' : 'Share plan'}
                    aria-label={quickShareUrl ? 'Manage sharing' : 'Share plan'}
                  >
                    {sharePlanIcon}
                  </button>
                </div>
              </div>
            </div>
          </aside>
        </div>
        <div className="border-t border-gray-100 bg-gray-50 px-5 py-3 text-sm leading-relaxed text-gray-600 sm:px-8">
          AI plans are a planning aid. Verify current prices, item availability, dietary needs, safety requirements, and venue rules before buying or hosting.
        </div>
      </section>

      {/* Modern Responsive Horizontal Segmented Navigation Bar */}
      <div className="w-full">
        <nav
          ref={planTabsRef}
          aria-label="Plan navigation tabs"
          className="sticky top-[108px] md:top-20 z-40 mb-6 rounded-2xl border border-stone-200/90 bg-white/95 p-1.5 shadow-sm backdrop-blur-md"
        >
          <div className="flex items-center gap-1.5 overflow-x-auto pb-0.5 scrollbar-none">
            {planNavTabs.map((tab) => {
              const isActive = isPlanTabActive(tab.id);
              return (
                <button
                  key={`nav-${tab.id}`}
                  type="button"
                  onClick={() => {
                    setActiveTab(tab.id);
                    if (planTabContentTopRef.current) {
                      planTabContentTopRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
                    }
                  }}
                  className={`flex items-center gap-1.5 sm:gap-2 px-3.5 sm:px-4 py-2 sm:py-2.5 rounded-xl text-xs sm:text-sm font-bold transition-all whitespace-nowrap shrink-0 active:scale-[0.98] ${
                    isActive
                      ? 'bg-stone-900 text-white shadow-sm'
                      : 'bg-transparent text-stone-600 hover:bg-stone-100 hover:text-stone-900'
                  }`}
                >
                  <span className="text-sm sm:text-base">{tab.icon || '📌'}</span>
                  <span>{tab.label}</span>
                </button>
              );
            })}
          </div>
        </nav>

        <div>

      {/* Tab Content */}
      <div ref={tabContentRef} className="min-h-[560px] bg-white rounded-b-xl shadow-lg scroll-mt-24 [overflow-anchor:none]">
        <div ref={planTabContentTopRef} className="scroll-mt-[180px]" />
        {windowWidth < 1024 && isMobileDetailView && (
          <div className="sticky top-[110px] md:top-24 z-30 bg-white/95 backdrop-blur border-b border-gray-200 px-4 py-2.5 flex items-center gap-3">
            <button
              type="button"
              onClick={() => setActiveTab('overview')}
              className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-700 active:scale-95 transition-transform flex items-center justify-center border border-gray-200 bg-white shadow-sm"
            >
              <svg className="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M15 19l-7-7 7-7" />
              </svg>
            </button>
            <span className="font-extrabold text-gray-950 text-base capitalize">
              {(() => {
                const labelMap = {
                  timeline: 'Timeline & Setup',
                  budget: 'Budget Details',
                  shopping: 'Shopping List',
                  sourcing: 'Sourcing & Vendors',
                  menu: 'Food Menu',
                  activities: 'Activities & Games',
                  colors: 'Theme Colors',
                  dress: 'Dress Code',
                  logistics: 'Logistics'
                };
                return labelMap[activeTab] || activeTab;
              })()}
            </span>
          </div>
        )}
        {windowWidth < 1024 && !isMobileDetailView ? (
          <>
            {/* Mobile Dashboard */}
            <div id="plan-tab-panel-dashboard-mobile" className="flex flex-col p-4 sm:p-6 space-y-5 bg-gray-50/30">
              {/* Planner Review / Fit Card */}
              {hasReviewData && (
                <div className="mb-2 rounded-2xl border border-emerald-100 bg-emerald-50/60 p-4 shadow-sm flex flex-col sm:flex-row sm:items-center gap-4">
                  {hasValidScore && (
                    <div className="flex flex-col items-center justify-center bg-white border border-emerald-200 h-14 w-14 rounded-2xl shadow-sm shrink-0 mx-auto sm:mx-0">
                      <span className="text-xl font-black text-emerald-600 leading-none">
                        {parsedCriticScore.toFixed(1)}
                      </span>
                      <span className="text-[8px] font-bold text-emerald-500 uppercase mt-0.5">fit score</span>
                    </div>
                  )}
                  <div className="min-w-0 flex-1 text-center sm:text-left">
                    <span className="block text-[10px] font-bold uppercase tracking-wider text-emerald-800 mb-0.5">Planner Review</span>
                    {plannerReview && (
                      <p className="text-xs text-emerald-950 font-medium leading-relaxed font-sans">
                        {plannerReview}
                      </p>
                    )}
                    {plannerBadges.length > 0 && (
                      <div className="mt-2 flex flex-wrap justify-center sm:justify-start gap-1">
                        {plannerBadges.map((badge) => (
                          <span key={badge} className="inline-flex items-center rounded-full bg-white border border-emerald-100 px-2.5 py-0.5 text-[9px] font-bold uppercase tracking-wider text-emerald-700">
                            ✓ {badge}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Budget Gauge Card */}
              {hasProvidedBudget && isTabAllowed('budget') && (
                <div className="mb-2 rounded-2xl border border-gray-200 bg-white p-4 shadow-sm">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-xs font-bold uppercase tracking-wider text-gray-500">Budget Tracker</span>
                    <span className="text-xs font-bold text-amber-700">
                      {formatEstimatedMoney(optimizedTotal)} / {budgetSummary}
                    </span>
                  </div>
                  {(() => {
                    const pct = Math.min(100, Math.round((optimizedTotal / Number(eventData.budget)) * 100));
                    const isOver = optimizedTotal > Number(eventData.budget);
                    return (
                      <div>
                        <div className="w-full bg-gray-200 h-2 rounded-full overflow-hidden">
                          <div
                            className={`h-full rounded-full transition-all duration-500 ${isOver ? 'bg-red-500' : 'bg-zinc-900'}`}
                            style={{ width: `${pct}%` }}
                          />
                        </div>
                        <p className="text-[10px] text-gray-500 mt-1.5">
                          {isOver
                            ? `⚠️ Spent ${formatEstimatedMoney(optimizedTotal - Number(eventData.budget))} over budget`
                            : `${pct}% of total budget allocated.`}
                        </p>
                      </div>
                    );
                  })()}
                </div>
              )}

              {/* Next Best Actions */}
              <section className="rounded-2xl border border-gray-200 bg-white p-4 shadow-sm">
                <p className="text-xs font-bold uppercase tracking-[0.18em] text-stone-500">Plan hub</p>
                <h3 className="mt-1 text-lg font-extrabold text-gray-950">Next best actions</h3>
                <div className="mt-3 divide-y divide-gray-200 overflow-hidden rounded-xl border border-gray-200 bg-white">
                  {nextActions.map((action) => (
                    <button
                      key={action.title}
                      type="button"
                      onClick={action.onClick}
                      className="grid w-full gap-1 p-3 text-left transition hover:bg-stone-50"
                    >
                      <span className="block text-sm font-extrabold text-gray-950">{action.title}</span>
                      <span className="block text-[11px] text-gray-600 leading-normal">{action.body}</span>
                      <span className="text-xs font-bold text-stone-600 mt-0.5">{action.action}</span>
                    </button>
                  ))}
                </div>
              </section>

              {/* Grid of Section Tiles */}
              <section className="space-y-2.5">
                <p className="text-xs font-bold uppercase tracking-[0.18em] text-gray-500">Plan Sections</p>
                <div className="grid grid-cols-2 gap-3">
                  {isTabAllowed('timeline') && (
                    <button
                      type="button"
                      onClick={() => setActiveTab('timeline')}
                      className="flex flex-col items-start p-4 bg-white border border-gray-200 rounded-2xl shadow-sm hover:border-stone-300 hover:shadow-md transition text-left"
                    >
                      <span className="text-2xl mb-1.5">📅</span>
                      <span className="text-xs font-bold text-gray-900">Timeline & Setup</span>
                      <span className="text-[10px] text-gray-500 mt-1 font-semibold">{timelineBlocks.length} phases</span>
                    </button>
                  )}

                  {isTabAllowed('budget') && (
                    <button
                      type="button"
                      onClick={() => setActiveTab('budget')}
                      className="flex flex-col items-start p-4 bg-white border border-gray-200 rounded-2xl shadow-sm hover:border-stone-300 hover:shadow-md transition text-left"
                    >
                      <span className="text-2xl mb-1.5">💸</span>
                      <span className="text-xs font-bold text-gray-900">Budget Details</span>
                      <span className="text-[10px] text-gray-500 mt-1 font-semibold">{budgetSummary}</span>
                    </button>
                  )}

                  {isTabAllowed('shopping') && (
                    <button
                      type="button"
                      onClick={() => setActiveTab('shopping')}
                      className="flex flex-col items-start p-4 bg-white border border-gray-200 rounded-2xl shadow-sm hover:border-stone-300 hover:shadow-md transition text-left"
                    >
                      <span className="text-2xl mb-1.5">🛒</span>
                      <span className="text-xs font-bold text-gray-900">Shopping List</span>
                      <span className="text-[10px] text-gray-500 mt-1 font-semibold">{normalizedItems.length} items</span>
                    </button>
                  )}

                  {isTabAllowed('sourcing') && (
                    <button
                      type="button"
                      onClick={() => setActiveTab('sourcing')}
                      className="flex flex-col items-start p-4 bg-white border border-gray-200 rounded-2xl shadow-sm hover:border-stone-300 hover:shadow-md transition text-left"
                    >
                      <span className="text-2xl mb-1.5">🏪</span>
                      <span className="text-xs font-bold text-gray-900">Sourcing / Vendors</span>
                      <span className="text-[10px] text-gray-500 mt-1 font-semibold">Links & catalog</span>
                    </button>
                  )}

                  {isTabAllowed('menu') && (
                    <button
                      type="button"
                      onClick={() => setActiveTab('menu')}
                      className="flex flex-col items-start p-4 bg-white border border-gray-200 rounded-2xl shadow-sm hover:border-stone-300 hover:shadow-md transition text-left"
                    >
                      <span className="text-2xl mb-1.5">🍽️</span>
                      <span className="text-xs font-bold text-gray-900">Food Menu</span>
                      <span className="text-[10px] text-gray-500 mt-1 font-semibold">{foodItems.length} items</span>
                    </button>
                  )}

                  {isTabAllowed('activities') && (
                    <button
                      type="button"
                      onClick={() => setActiveTab('activities')}
                      className="flex flex-col items-start p-4 bg-white border border-gray-200 rounded-2xl shadow-sm hover:border-stone-300 hover:shadow-md transition text-left"
                    >
                      <span className="text-2xl mb-1.5">🎭</span>
                      <span className="text-xs font-bold text-gray-900">Activities & Games</span>
                      <span className="text-[10px] text-gray-500 mt-1 font-semibold">{activityItems.length} items</span>
                    </button>
                  )}

                  {isTabAllowed('colors') && (
                    <button
                      type="button"
                      onClick={() => setActiveTab('colors')}
                      className="flex flex-col items-start p-4 bg-white border border-gray-200 rounded-2xl shadow-sm hover:border-stone-300 hover:shadow-md transition text-left"
                    >
                      <span className="text-2xl mb-1.5">🎨</span>
                      <span className="text-xs font-bold text-gray-900">Theme Colors</span>
                      <span className="text-[10px] text-gray-500 mt-1 font-semibold">{displayedPalette.length} shades</span>
                    </button>
                  )}

                  {isTabAllowed('dress') && (
                    <button
                      type="button"
                      onClick={() => setActiveTab('dress')}
                      className="flex flex-col items-start p-4 bg-white border border-gray-200 rounded-2xl shadow-sm hover:border-stone-300 hover:shadow-md transition text-left"
                    >
                      <span className="text-2xl mb-1.5">👗</span>
                      <span className="text-xs font-bold text-gray-900">Dress Code</span>
                      <span className="text-[10px] text-gray-500 mt-1 font-semibold">Attire guide</span>
                    </button>
                  )}

                  {isTabAllowed('logistics') && (
                    <button
                      type="button"
                      onClick={() => setActiveTab('logistics')}
                      className="flex flex-col items-start p-4 bg-white border border-gray-200 rounded-2xl shadow-sm hover:border-stone-300 hover:shadow-md transition text-left"
                    >
                      <span className="text-2xl mb-1.5">🚚</span>
                      <span className="text-xs font-bold text-gray-900">Logistics</span>
                      <span className="text-[10px] text-gray-500 mt-1 font-semibold">{logisticsChecklist.length} tasks</span>
                    </button>
                  )}
                </div>
              </section>

              {isThemeIdeasOnlyPlan && (
                <div className="mt-4 space-y-3">
                  <p className="text-xs font-bold uppercase tracking-[0.18em] text-gray-500">Theme Concepts</p>
                  <div className="flex flex-col gap-3">
                    {Object.entries(themePlans).map(([key, snapshot]) => {
                      const planTheme = snapshot.selected_theme;
                      if (!planTheme) return null;
                      const planPalette = normalizePalette(planTheme.color_palette);
                      const isCurrent = key === currentThemePlanKey;
                      return (
                        <div key={key} className={`rounded-2xl border p-4 bg-white shadow-sm flex flex-col justify-between ${isCurrent ? 'border-zinc-900 ring-1 ring-zinc-900' : 'border-gray-200'}`}>
                          <div>
                            <div className="flex items-start justify-between mb-1.5 gap-2">
                              <h4 className="font-bold text-gray-950 text-sm leading-snug">{planTheme.name || 'Untitled Concept'}</h4>
                              {isCurrent && (
                                <span className="rounded-full bg-stone-100 text-stone-700 px-2 py-0.5 text-[8px] font-bold uppercase tracking-wider shrink-0">Active</span>
                              )}
                            </div>
                            <p className="text-xs text-gray-600 leading-relaxed mb-3">{planTheme.description}</p>
                          </div>
                          <div className="flex items-center justify-between mt-1">
                            <div className="flex gap-1">
                              {planPalette.slice(0, 4).map((color, idx) => (
                                <span key={`${color.hex}-${idx}`} className="h-5 w-5 rounded-full ring-1 ring-gray-200 shadow-sm" style={{ backgroundColor: color.hex }} />
                              ))}
                            </div>
                            {!isCurrent && (
                              <button
                                type="button"
                                onClick={() => onViewThemePlan?.(planTheme)}
                                className="py-1.5 px-3 bg-stone-100 text-stone-800 hover:bg-stone-200 text-[11px] font-bold rounded-lg transition"
                              >
                                Select
                              </button>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          </>
        ) : (
          <>
            {/* ── Desktop Overview Hero Banner ─────────────────────────────── */}
            {activeTab === 'overview' && !isThemeIdeasOnlyPlan && (
              <div id="plan-tab-panel-overview" className="flex flex-col p-4 sm:p-6 md:p-8">
                {/* Planner Review / Fit Card — enhanced */}
                {hasReviewData && (
                  <div className="mb-6 card-editorial flex flex-col sm:flex-row sm:items-start gap-5 p-5 sm:p-6 overflow-hidden">
                    {hasValidScore && (
                      <div className="shrink-0 flex flex-col items-center justify-center mx-auto sm:mx-0">
                        <div className="relative h-20 w-20">
                          {/* Score ring */}
                          <svg className="absolute inset-0" viewBox="0 0 80 80">
                            <circle cx="40" cy="40" r="34" fill="none" stroke="#E7E5E4" strokeWidth="6" />
                            <circle
                              cx="40" cy="40" r="34" fill="none"
                              stroke="#059669" strokeWidth="6"
                              strokeLinecap="round"
                              strokeDasharray={`${Math.round((parsedCriticScore / 10) * 213.6)} 213.6`}
                              transform="rotate(-90 40 40)"
                            />
                          </svg>
                          <div className="absolute inset-0 flex flex-col items-center justify-center">
                            <span className="text-2xl font-black text-emerald-600 leading-none">{parsedCriticScore.toFixed(1)}</span>
                            <span className="text-[8px] font-bold text-emerald-500 uppercase tracking-wider">/ 10</span>
                          </div>
                        </div>
                        <span className="mt-1.5 text-[9px] font-bold uppercase tracking-wider text-stone-500">Fit score</span>
                      </div>
                    )}
                    <div className="flex-1 min-w-0">
                      <p className="section-eyebrow text-emerald-700">AI planner review</p>
                      {plannerReview && (
                        <p className="mt-2 text-sm leading-relaxed text-gray-800 max-w-2xl">{plannerReview}</p>
                      )}
                      {plannerBadges.length > 0 && (
                        <div className="mt-3 flex flex-wrap gap-1.5">
                          {plannerBadges.map((badge) => (
                            <span key={badge} className="badge-sage">✓ {badge}</span>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Stats row */}
                <div className="mb-6 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
                  <button
                    type="button"
                    onClick={() => setActiveTab('budget')}
                    className="card-editorial lift-card p-4 text-left focus-ring"
                  >
                    <p className="section-eyebrow">Estimated spend</p>
                    <p className="mt-2 text-2xl font-black tracking-tight text-gray-950">{formatEstimatedMoney(optimizedTotal)}</p>
                    <p className={`mt-1 text-xs font-bold ${budgetToneTextClass}`}>{budgetDeltaLabel}</p>
                  </button>
                  <button
                    type="button"
                    onClick={() => setActiveTab('shopping')}
                    className="card-editorial lift-card p-4 text-left focus-ring"
                  >
                    <p className="section-eyebrow">Shopping items</p>
                    <p className="mt-2 text-2xl font-black tracking-tight text-gray-950">{normalizedItems.length}</p>
                    <p className="mt-1 text-xs font-semibold text-stone-500">{formatEstimatedMoney(mustHaveTotal)} in essentials</p>
                  </button>
                  <button
                    type="button"
                    onClick={() => isTabAllowed('activities') ? setActiveTab('activities') : isTabAllowed('menu') ? setActiveTab('menu') : null}
                    className="card-editorial lift-card p-4 text-left focus-ring"
                  >
                    <p className="section-eyebrow">Activities & menu</p>
                    <p className="mt-2 text-2xl font-black tracking-tight text-gray-950">{activityItems.length + foodItems.length}</p>
                    <p className="mt-1 text-xs font-semibold text-stone-500">experience items</p>
                  </button>
                  <button
                    type="button"
                    onClick={() => setActiveTab('timeline')}
                    className="card-editorial lift-card p-4 text-left focus-ring"
                  >
                    <p className="section-eyebrow">Timeline</p>
                    <p className="mt-2 text-2xl font-black tracking-tight text-gray-950">{timelineBlocks.length}</p>
                    <p className="mt-1 text-xs font-semibold text-stone-500">planning phases</p>
                  </button>
                </div>

                {/* Main content: Next actions + Export */}
                <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_minmax(0,320px)]">
                  {/* Next best actions */}
                  <section className="card-editorial overflow-hidden">
                    <div className="border-b border-stone-100 bg-stone-50/60 px-5 py-4">
                      <p className="section-eyebrow">Plan hub</p>
                      <h3 className="mt-1 text-lg font-extrabold text-gray-950">Next best actions</h3>
                    </div>
                    <div className="divide-y divide-stone-100">
                      {nextActions.map((action) => (
                        <button
                          key={action.title}
                          type="button"
                          onClick={action.onClick}
                          className="grid w-full gap-1 p-4 text-left transition hover:bg-stone-50 sm:grid-cols-[1fr_auto] sm:items-center group"
                        >
                          <span>
                            <span className="block text-sm font-extrabold text-gray-950">{action.title}</span>
                            <span className="mt-0.5 block text-xs leading-relaxed text-gray-500">{action.body}</span>
                          </span>
                          <span className="text-xs font-bold text-stone-500 group-hover:text-stone-800 transition">{action.action} →</span>
                        </button>
                      ))}
                    </div>
                  </section>

                  {/* Export & Share sidebar */}
                  <aside className="card-editorial p-5 sm:p-6 flex flex-col gap-4">
                    <div>
                      <p className="section-eyebrow">Export & share</p>
                      <h3 className="mt-1 text-base font-extrabold text-gray-950">Take the plan with you</h3>
                      <p className="mt-1.5 text-xs leading-relaxed text-stone-500">
                        Share with co-hosts, print a setup checklist, or browse catalog picks for best-value buys.
                      </p>
                    </div>
                    <div className="flex flex-col gap-2.5 mt-auto">
                      <button
                        type="button"
                        onClick={() => setShowSocialStoryModal(true)}
                        className="flex h-11 items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-purple-600 via-pink-600 to-rose-500 px-4 text-sm font-bold text-white transition hover:opacity-95 shadow-sm active:scale-[0.98]"
                      >
                        <span>📸</span>
                        <span>Share 9:16 Story Card</span>
                      </button>
                      <button
                        type="button"
                        onClick={() => setShowInviteModal(true)}
                        className="flex h-11 items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-pink-500 to-rose-500 px-4 text-sm font-bold text-white transition hover:from-pink-600 hover:to-rose-600 shadow-sm active:scale-[0.98]"
                      >
                        <span>💌</span>
                        <span>Digital Invites & RSVP</span>
                      </button>
                      <button
                        type="button"
                        onClick={() => setShowBulkCartModal(true)}
                        className="flex h-11 items-center justify-center gap-2 rounded-xl bg-amber-500 hover:bg-amber-600 px-4 text-sm font-bold text-stone-950 transition shadow-sm active:scale-[0.98]"
                      >
                        <span>🛒</span>
                        <span>1-Click Cart Export</span>
                      </button>
                      <button
                        type="button"
                        onClick={handleSharePlan}
                        className="flex h-11 items-center justify-center gap-2 rounded-xl bg-stone-900 px-4 text-sm font-bold text-white transition hover:bg-stone-700 active:scale-[0.98]"
                      >
                        <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                          <path strokeLinecap="round" strokeLinejoin="round" d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z" />
                        </svg>
                        Share plan
                      </button>
                      <button
                        type="button"
                        onClick={() => openCatalogExplorer({ tab: 'items' })}
                        className="flex h-11 items-center justify-center gap-2 rounded-xl border border-stone-200 bg-stone-50 px-4 text-sm font-bold text-stone-800 transition hover:bg-stone-100 active:scale-[0.98]"
                      >
                        Browse catalog picks
                      </button>
                      <button
                        type="button"
                        onClick={handlePrintChecklist}
                        className="flex h-11 items-center justify-center gap-2 rounded-xl border border-stone-200 bg-white px-4 text-sm font-bold text-stone-700 transition hover:bg-stone-50 active:scale-[0.98]"
                      >
                        Print checklist
                      </button>
                    </div>
                  </aside>
                </div>
              </div>
            )}

        {activeTab === 'timeline' && (
          <div id="plan-tab-panel-timeline" className="p-4 sm:p-6 md:p-8">
            {!isTabAllowed('timeline') ? (
              <NotIncludedState title="Timeline" />
            ) : timelineBlocks.length === 0 ? (
              <MissingDataState title="Timeline" />
            ) : (
              <>
                {/* Header */}
                <div className="mb-8">
                  <p className="section-eyebrow">Run of show</p>
                  <h3 className="mt-2 text-2xl font-extrabold tracking-tight text-gray-950">Your event timeline</h3>
                  <p className="mt-2 max-w-2xl text-sm leading-relaxed text-gray-500">
                    Check off tasks as you complete them. Adjust timing based on shipping lead times, rentals, and how much DIY you're taking on.
                  </p>
                </div>

                {/* Phases with connector line */}
                <div className="relative">
                  {/* Vertical connector line */}
                  <div className="absolute left-[23px] top-6 bottom-6 w-0.5 bg-gradient-to-b from-stone-300 via-stone-200 to-transparent hidden sm:block" />

                  <div className="space-y-5">
                    {timelineBlocks.map((block, index) => {
                      const blockId = `block-${index}`;
                      const tasks = Array.isArray(block.tasks) ? block.tasks : [];
                      return (
                        <div key={`${block.time}-${index}`} className="relative sm:pl-14">
                          {/* Phase dot */}
                          <div className="absolute left-0 top-5 hidden sm:flex h-12 w-12 items-center justify-center rounded-full bg-stone-900 text-white text-lg font-black shadow-md ring-4 ring-white z-10">
                            {index + 1}
                          </div>

                          {/* Phase card */}
                          <div className="card-editorial overflow-hidden">
                            {/* Phase header */}
                            <div className="flex items-start justify-between gap-3 border-b border-stone-100 bg-stone-50/60 px-5 py-4">
                              <div>
                                <p className="text-xs font-bold uppercase tracking-[0.16em] text-stone-500">{block.time}</p>
                                <h4 className="mt-0.5 text-base font-extrabold text-gray-950">{block.title}</h4>
                                {block.calendarDate && (
                                  <p className="mt-0.5 text-xs font-semibold text-stone-500">{block.calendarDate}</p>
                                )}
                              </div>
                              {tasks.length > 0 && (
                                <span className="badge-stone shrink-0 mt-0.5">{tasks.length} tasks</span>
                              )}
                            </div>

                            {/* Tasks as interactive checkboxes */}
                            {tasks.length > 0 && (
                              <ul className="divide-y divide-stone-100 px-5">
                                {tasks.map((task, taskIdx) => {
                                  const taskKey = `${blockId}-task-${taskIdx}`;
                                  return (
                                    <li key={taskKey} className="flex items-start gap-3 py-3 group">
                                      <button
                                        type="button"
                                        id={`timeline-task-${index}-${taskIdx}`}
                                        aria-label={`Mark "${task}" as done`}
                                        onClick={(e) => {
                                          const btn = e.currentTarget;
                                          const isChecked = btn.getAttribute('data-checked') === 'true';
                                          btn.setAttribute('data-checked', (!isChecked).toString());
                                          btn.classList.toggle('bg-stone-900', !isChecked);
                                          btn.classList.toggle('border-stone-900', !isChecked);
                                          btn.classList.toggle('bg-white', isChecked);
                                          btn.classList.toggle('border-stone-300', isChecked);
                                          const checkIcon = btn.querySelector('.check-icon');
                                          if (checkIcon) {
                                            checkIcon.style.opacity = isChecked ? '0' : '1';
                                          }
                                          const label = btn.closest('li')?.querySelector('.task-label');
                                          if (label) {
                                            label.classList.toggle('line-through', !isChecked);
                                            label.classList.toggle('text-stone-400', !isChecked);
                                            label.classList.toggle('text-gray-700', isChecked);
                                          }
                                        }}
                                        data-checked="false"
                                        className="mt-0.5 h-5 w-5 shrink-0 rounded-md border-2 border-stone-300 bg-white transition-all duration-150 hover:border-stone-500 focus-ring flex items-center justify-center"
                                      >
                                        <svg
                                          className="check-icon h-3 w-3 text-white"
                                          style={{ opacity: 0, transition: 'opacity 0.15s' }}
                                          fill="none"
                                          viewBox="0 0 12 12"
                                          stroke="currentColor"
                                          strokeWidth={2.5}
                                        >
                                          <path strokeLinecap="round" strokeLinejoin="round" d="M2 6l3 3 5-5" />
                                        </svg>
                                      </button>
                                      <span className="task-label flex-1 text-sm leading-relaxed text-gray-700 transition-all duration-200">
                                        {task}
                                      </span>
                                    </li>
                                  );
                                })}
                              </ul>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>

                {/* Footer tip */}
                <div className="mt-8 rounded-2xl border border-amber-200 bg-amber-50 px-5 py-4">
                  <p className="text-xs font-bold uppercase tracking-wider text-amber-800 mb-1">Planning tip</p>
                  <p className="text-sm leading-relaxed text-amber-900">
                    Use this as your execution roadmap. Confirm vendor bookings early, especially for custom cakes, balloon artists, or venue add-ons that need lead time.
                  </p>
                </div>
              </>
            )}
          </div>
        )}


        {activeTab === 'budget' && (
          <div id="plan-tab-panel-budget" className="p-4 sm:p-6 md:p-8">
            {!isTabAllowed('budget') ? (
              <NotIncludedState title="Budget" />
            ) : categorySpend.length === 0 ? (
              <MissingDataState title="Budget" />
            ) : (
              <div className="space-y-6">
                {/* Section header */}
                <div>
                  <p className="section-eyebrow">Cost breakdown</p>
                  <h3 className="mt-2 text-2xl font-extrabold tracking-tight text-gray-950">Budget analyzer</h3>
                  <p className="mt-1.5 text-sm leading-relaxed text-gray-500">
                    Estimated costs based on your guest count, event type, and shopping list. Prices are approximate — verify before purchasing.
                  </p>
                </div>

                {/* Hero metric cards */}
                <div className="grid gap-3 sm:grid-cols-3">
                  {/* Total spend */}
                  <div className="card-editorial p-5">
                    <p className="section-eyebrow">Estimated spend</p>
                    <p className="mt-2 text-3xl font-black tracking-tight text-gray-950">{formatEstimatedMoney(optimizedTotal)}</p>
                    <p className={`mt-1.5 text-sm font-bold ${budgetToneTextClass}`}>{budgetDeltaLabel}</p>
                    {budgetNumber > 0 && (
                      <div className="mt-3">
                        <div className="flex items-center justify-between mb-1.5">
                          <span className="text-[10px] font-bold uppercase tracking-wider text-stone-500">Budget used</span>
                          <span className="text-[10px] font-bold text-stone-700">{Math.round((optimizedTotal / budgetNumber) * 100)}%</span>
                        </div>
                        <div className="h-1.5 w-full overflow-hidden rounded-full bg-stone-100">
                          <div
                            className={`h-full rounded-full transition-all duration-700 ${optimizedTotal > budgetNumber ? 'bg-red-500' : 'bg-stone-900'}`}
                            style={{ width: `${Math.min(100, Math.round((optimizedTotal / budgetNumber) * 100))}%` }}
                          />
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Rent savings */}
                  <div className="card-editorial p-5">
                    <p className="section-eyebrow">Rent vs. buy savings</p>
                    <p className="mt-2 text-3xl font-black tracking-tight text-gray-950">{formatEstimatedMoney(rentSavings)}</p>
                    <p className="mt-1.5 text-xs leading-relaxed text-stone-500">
                      {rentSavings > 0
                        ? 'Estimated savings by renting high-cost items instead of buying.'
                        : 'Most items are buy-once essentials — no major rental swaps flagged.'}
                    </p>
                  </div>

                  {/* Flex / optional spend */}
                  <div className="card-editorial p-5">
                    <p className="section-eyebrow">Optional extras</p>
                    <p className="mt-2 text-3xl font-black tracking-tight text-gray-950">{formatEstimatedMoney(optionalTotal)}</p>
                    <p className="mt-1.5 text-xs leading-relaxed text-stone-500">
                      Nice-to-have items. Cut these first if you need to trim the plan.
                    </p>
                  </div>
                </div>

                {/* Category breakdown */}
                <div className="card-editorial overflow-hidden">
                  <div className="border-b border-stone-100 bg-stone-50/60 px-5 py-4">
                    <h4 className="text-base font-extrabold text-gray-950">Spend by category</h4>
                    <p className="mt-0.5 text-xs text-stone-500">Each bar shows % of your total estimated spend.</p>
                  </div>
                  <div className="divide-y divide-stone-100">
                    {categorySpend.map((category, idx) => {
                      const percent = Math.min(100, Math.round((category.optimizedTotal / Math.max(optimizedTotal, 1)) * 100));
                      const catColors = [
                        'bg-stone-900', 'bg-amber-600', 'bg-emerald-600', 'bg-violet-600',
                        'bg-rose-500', 'bg-sky-600', 'bg-orange-500', 'bg-teal-600'
                      ];
                      const barColor = catColors[idx % catColors.length];
                      return (
                        <div key={category.name} className="px-5 py-4">
                          <div className="flex items-center justify-between gap-3 mb-2.5">
                            <div className="flex items-center gap-2.5 min-w-0">
                              <span className={`h-2.5 w-2.5 shrink-0 rounded-full ${barColor}`} />
                              <div className="min-w-0">
                                <p className="font-bold text-sm text-gray-950 truncate">{category.name}</p>
                                <p className="text-xs text-stone-500">{category.count} item{category.count === 1 ? '' : 's'}</p>
                              </div>
                            </div>
                            <div className="shrink-0 text-right">
                              <p className="text-base font-extrabold text-gray-950">{formatEstimatedMoney(category.optimizedTotal)}</p>
                              <p className="text-[10px] font-bold text-stone-500">{percent}% of total</p>
                            </div>
                          </div>
                          <div className="h-2 w-full overflow-hidden rounded-full bg-stone-100">
                            <div
                              className={`h-full rounded-full transition-all duration-700 ${barColor}`}
                              style={{ width: `${percent}%` }}
                            />
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>

                {/* Budget health & savings tips */}
                <div className="grid gap-4 lg:grid-cols-2">
                  {/* Health card */}
                  <div className={`rounded-2xl border p-5 ${budgetDelta < 0 ? 'border-amber-200 bg-amber-50' : 'border-emerald-200 bg-emerald-50'}`}>
                    <p className={`section-eyebrow ${budgetDelta < 0 ? 'text-amber-700' : 'text-emerald-700'}`}>Budget health</p>
                    <p className={`mt-2 text-lg font-extrabold ${budgetDelta < 0 ? 'text-amber-900' : 'text-emerald-900'}`}>
                      {!budgetNumber
                        ? 'No budget set'
                        : budgetDelta < 0 && budgetOverRatio <= 0.03
                        ? 'Slightly over — no panic trim needed'
                        : budgetDelta < 0
                        ? 'Over budget — review optional items'
                        : 'On track — cushion available'}
                    </p>
                    <p className={`mt-2 text-sm leading-relaxed ${budgetDelta < 0 ? 'text-amber-800' : 'text-emerald-800'}`}>
                      {!budgetNumber
                        ? 'Add a budget in Event Details to unlock trim and cushion guidance.'
                        : budgetDelta < 0
                        ? `You're ${formatEstimatedMoney(Math.abs(budgetDelta))} over your target. Start by removing optional items from the shopping list.`
                        : `You have ${formatEstimatedMoney(budgetDelta)} of cushion. This covers last-minute additions, price changes, or upgrades.`}
                    </p>
                  </div>

                  {/* Savings tips */}
                  <div className="rounded-2xl border border-stone-200 bg-white p-5">
                    <p className="section-eyebrow text-stone-500">Smart savings tips</p>
                    <ul className="mt-3 space-y-2.5">
                      {[
                        rentSavings > 0 ? `Rent instead of buying on key items — saves ~${formatEstimatedMoney(rentSavings)}` : null,
                        optionalTotal > 0 ? `Cut optional extras to save up to ${formatEstimatedMoney(optionalTotal)}` : null,
                        'Buy in bulk for consumables (cups, napkins, balloons)',
                        'DIY signage and table runners save $30–$80 on average',
                        'Source decor from Facebook Marketplace or thrift stores first',
                      ].filter(Boolean).slice(0, 4).map((tip, i) => (
                        <li key={i} className="flex items-start gap-2.5 text-sm text-gray-700">
                          <span className="mt-1 h-1.5 w-1.5 shrink-0 rounded-full bg-stone-400" />
                          {tip}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>

                {/* Disclaimer */}
                <p className="text-xs leading-relaxed text-stone-400">
                  * All estimates are based on typical retail pricing for your region. Actual costs may vary. Verify prices before committing to purchases or vendor bookings.
                </p>
              </div>
            )}
          </div>
        )}


        {activeTab === 'shopping' && (
          <div id="plan-tab-panel-shopping">
            {!isTabAllowed('shopping') ? (
              <div className="p-4 sm:p-6 md:p-8">
                <NotIncludedState title="Shopping" />
              </div>
            ) : normalizedItems.length === 0 ? (
              <div className="p-4 sm:p-6 md:p-8">
                <MissingDataState title="Shopping List" />
              </div>
            ) : (
              <ShoppingList
                theme={theme}
                eventData={eventData}
                items={items}
                setItems={setItems}
                onShare={onShare}
                planId={planId}
                previousView={previousView}
                sharePermissions={sharePermissions}
                isSharedView={isSharedView}
                inTab={true}
                expandedCategories={expandedCategories}
                setExpandedCategories={setExpandedCategories}
                showCuratedOptions={false}
                onItemLinksChanged={onItemLinksChanged}
              />
            )}
          </div>
        )}
        {activeTab === 'moodboard' && (
          <div id="plan-tab-panel-moodboard" className="p-4 sm:p-6 md:p-8">
            <SpatialMoodboard
              plan={{ ...eventData, shopping_list: normalizedItems, color_palette: displayedPalette }}
              theme={theme}
              onNavigateToShopping={() => setActiveTab('shopping')}
              onShareStory={() => setShowSocialStoryModal(true)}
            />
          </div>
        )}

        {activeTab === 'sourcing' && (
          <div id="plan-tab-panel-sourcing" className="p-4 sm:p-6 md:p-8">
            {!isTabAllowed('sourcing') ? (
              <NotIncludedState title="Sourcing" />
            ) : (
              <div className="mb-6 grid gap-4 lg:grid-cols-[minmax(0,1fr)_minmax(280px,0.55fr)]">
                <section className="rounded-2xl border border-stone-200 bg-stone-50/80 p-5 sm:p-6">
                  <p className="text-xs font-bold uppercase tracking-[0.18em] text-stone-500">Smart sourcing</p>
                  <h3 className="mt-2 text-2xl font-extrabold text-gray-950">Catalog-backed picks</h3>
                  <p className="mt-2 text-sm leading-relaxed text-gray-600">
                    Compare practical buys, DIY options, rentals, food vendors, and substitutions without digging through the shopping list first.
                  </p>
                  <div className="mt-5 grid gap-3 sm:grid-cols-2">
                    <button type="button" onClick={() => openCatalogExplorer({ tab: 'items', category: 'Decor' })} className="rounded-2xl border border-white bg-white p-4 text-left shadow-sm transition hover:border-stone-300 hover:bg-stone-50">
                      <h4 className="text-lg font-extrabold text-gray-950">Decor</h4>
                      <p className="mt-2 text-sm leading-relaxed text-gray-600">Atmosphere, lighting, backdrops, and table accents.</p>
                    </button>
                    <button type="button" onClick={() => openCatalogExplorer({ tab: 'items', category: 'Food & Cake' })} className="rounded-2xl border border-white bg-white p-4 text-left shadow-sm transition hover:border-stone-300 hover:bg-stone-50">
                      <h4 className="text-lg font-extrabold text-gray-950">Food</h4>
                      <p className="mt-2 text-sm leading-relaxed text-gray-600">Prepared food, grocery prep, bakery, and caterer routes.</p>
                    </button>
                    <button type="button" onClick={() => openCatalogExplorer({ tab: 'activities', category: 'All' })} className="rounded-2xl border border-white bg-white p-4 text-left shadow-sm transition hover:border-stone-300 hover:bg-stone-50">
                      <h4 className="text-lg font-extrabold text-gray-950">Activities</h4>
                      <p className="mt-2 text-sm leading-relaxed text-gray-600">Games, icebreakers, DIY stations, and required supplies.</p>
                    </button>
                    <button type="button" onClick={() => openCatalogExplorer({ tab: 'items', category: 'Rentals' })} className="rounded-2xl border border-white bg-white p-4 text-left shadow-sm transition hover:border-stone-300 hover:bg-stone-50">
                      <h4 className="text-lg font-extrabold text-gray-950">Rentals</h4>
                      <p className="mt-2 text-sm leading-relaxed text-gray-600">Only where budget, venue, and guest count make sense.</p>
                    </button>
                  </div>
                </section>
                <aside className="rounded-2xl border border-gray-200 bg-white p-5 sm:p-6">
                  <p className="text-xs font-bold uppercase tracking-[0.18em] text-gray-500">Rules used</p>
                  <ul className="mt-3 space-y-2 text-sm font-semibold leading-relaxed text-gray-600">
                    <li>Prices and quantities are estimates.</li>
                    <li>Prepared food uses restaurants, pickup, catering, bakery, or grocery paths.</li>
                    <li>Retail links are reserved for supplies, packaged groceries, and decor.</li>
                    <li>Large rentals are filtered for modest home-event budgets.</li>
                  </ul>
                </aside>
              </div>
            )}
          </div>
        )}

        {activeTab === 'details' && (
          <div id="plan-tab-panel-details" className="p-4 sm:p-6 md:p-8">
            <div className="mb-6">
              <p className="text-xs font-bold uppercase tracking-[0.18em] text-stone-500">Other details</p>
              <h3 className="mt-2 text-2xl font-extrabold text-gray-950">Support sections</h3>
              <p className="mt-2 max-w-2xl text-sm leading-relaxed text-gray-600">
                These are supporting sections. Open only what you need, when you need it.
              </p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3">
              {isTabAllowed('activities') && (
                <button type="button" onClick={() => setActiveTab('activities')} className="rounded-2xl border border-gray-200 bg-gray-50 p-4 text-left transition hover:border-stone-300 hover:bg-stone-50 sm:p-5">
                  <p className="text-xs font-bold uppercase tracking-wider text-gray-500">Activities</p>
                  <h4 className="mt-2 text-lg font-extrabold text-gray-950">Guest flow</h4>
                  <p className="mt-2 text-sm leading-relaxed text-gray-600">{activityItems.length || 'Add'} activity item{activityItems.length === 1 ? '' : 's'} and supplies.</p>
                </button>
              )}
              {isTabAllowed('menu') && (
                <button type="button" onClick={() => setActiveTab('menu')} className="rounded-2xl border border-gray-200 bg-gray-50 p-4 text-left transition hover:border-stone-300 hover:bg-stone-50 sm:p-5">
                  <p className="text-xs font-bold uppercase tracking-wider text-gray-500">Menu</p>
                  <h4 className="mt-2 text-lg font-extrabold text-gray-950">Food and drinks</h4>
                  <p className="mt-2 text-sm leading-relaxed text-gray-600">{foodItems.length || 'Add'} menu item{foodItems.length === 1 ? '' : 's'} with quantities and safety checks.</p>
                </button>
              )}
              {isTabAllowed('logistics') && (
                <button type="button" onClick={() => setActiveTab('logistics')} className="rounded-2xl border border-gray-200 bg-gray-50 p-4 text-left transition hover:border-stone-300 hover:bg-stone-50 sm:p-5">
                  <p className="text-xs font-bold uppercase tracking-wider text-gray-500">Logistics</p>
                  <h4 className="mt-2 text-lg font-extrabold text-gray-950">Setup and safety</h4>
                  <p className="mt-2 text-sm leading-relaxed text-gray-600">Venue flow, weather backup, ownership, rentals, and safety review.</p>
                </button>
              )}
              {isTabAllowed('dress') && (
                <button type="button" onClick={() => setActiveTab('dress')} className="rounded-2xl border border-gray-200 bg-gray-50 p-4 text-left transition hover:border-stone-300 hover:bg-stone-50 sm:p-5">
                  <p className="text-xs font-bold uppercase tracking-wider text-gray-500">Dress code</p>
                  <h4 className="mt-2 text-lg font-extrabold text-gray-950">Guest attire</h4>
                  <p className="mt-2 text-sm leading-relaxed text-gray-600">Review and edit style guidance by age group.</p>
                </button>
              )}
              {isTabAllowed('colors') && (
                <button type="button" onClick={() => setActiveTab('colors')} className="rounded-2xl border border-gray-200 bg-gray-50 p-4 text-left transition hover:border-stone-300 hover:bg-stone-50 sm:p-5">
                  <p className="text-xs font-bold uppercase tracking-wider text-gray-500">Color palette</p>
                  <h4 className="mt-2 text-lg font-extrabold text-gray-950">{colorPalette.length} colors</h4>
                  <div className="mt-3 flex flex-wrap gap-2">
                    {colorPalette.slice(0, 6).map((color, idx) => (
                      <span key={`${color.hex}-${idx}`} className="h-6 w-6 rounded-full ring-1 ring-gray-300 shadow-sm" style={{ backgroundColor: color.hex }} title={color.name || color.hex} />
                    ))}
                  </div>
                </button>
              )}
              {isTabAllowed('sourcing') && (
                <button type="button" onClick={() => openCatalogExplorer({ tab: 'items' })} className="rounded-2xl border border-stone-200 bg-stone-50 p-4 text-left transition hover:border-stone-300 hover:bg-white sm:p-5">
                  <p className="text-xs font-bold uppercase tracking-wider text-stone-600">Catalog</p>
                  <h4 className="mt-2 text-lg font-extrabold text-gray-950">Sourcing picks</h4>
                  <p className="mt-2 text-sm leading-relaxed text-gray-600">Find practical items, rentals, and substitutions.</p>
                </button>
              )}
            </div>
          </div>
        )}

        {activeTab === 'activities' && (
          <div id="plan-tab-panel-activities" className="p-4 sm:p-6 md:p-8">
            {!isTabAllowed('activities') ? (
              <NotIncludedState title="Activities" />
            ) : (
              <>
                <div className="mb-6">
                  <p className="text-xs font-bold uppercase tracking-[0.18em] text-stone-500">Activities</p>
                  <h3 className="mt-2 text-2xl font-extrabold text-gray-950">Guest experience plan</h3>
                  <p className="mt-2 max-w-2xl text-sm leading-relaxed text-gray-600">
                    Activity items should be concrete enough to buy, set up, and delegate. Add missing supplies from the catalog or shopping list.
                  </p>
                </div>
                {activityItems.length ? (
                  <div className="grid gap-4 md:grid-cols-2">
                    {activityItems.map((item) => (
                      <div key={item.id} className="rounded-2xl border border-gray-200 bg-gray-50 p-5">
                        <p className="text-xs font-bold uppercase tracking-wider text-gray-500">{item.category}</p>
                        <h4 className="mt-2 text-lg font-extrabold text-gray-950">{item.name}</h4>
                        {item.description && <p className="mt-2 text-sm leading-relaxed text-gray-600">{item.description}</p>}
                        <p className="mt-4 text-sm font-bold text-amber-700">{formatEstimatedMoney(item.optimizedPrice || item.buyPrice, {}, item)}</p>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="rounded-2xl border border-dashed border-gray-300 bg-gray-50 p-6 text-sm leading-relaxed text-gray-600">
                    No activity-specific items were detected yet. Use the catalog or shopping list to add games, crafts, favors, or entertainment supplies.
                  </div>
                )}
              </>
            )}
          </div>
        )}

        {activeTab === 'menu' && (
          <div id="plan-tab-panel-menu" className="p-4 sm:p-6 md:p-8">
            {!isTabAllowed('menu') ? (
              <NotIncludedState title="Menu" />
            ) : (
              <>
                <div className="mb-6">
                  <p className="text-xs font-bold uppercase tracking-[0.18em] text-stone-500">Menu</p>
                  <h3 className="mt-2 text-2xl font-extrabold text-gray-950">Food and drink planning</h3>
                  <p className="mt-2 max-w-2xl text-sm leading-relaxed text-gray-600">
                    Keep food proportionate to {guestCount || 'your'} guests, and verify dietary needs, allergens, temperatures, and venue rules before buying.
                  </p>
                </div>
                {foodItems.length ? (
                  <div className="space-y-4">
                    <h4 className="text-sm font-bold uppercase tracking-wider text-gray-500 border-b border-gray-150 pb-2 flex items-center justify-between">
                      <span>Menu Items</span>
                      <span className="text-xs font-normal normal-case text-gray-500 bg-gray-100 px-2 py-0.5 rounded-full">
                        {foodItems.length} {foodItems.length === 1 ? 'item' : 'items'}
                      </span>
                    </h4>
                    <div className="grid gap-4 md:grid-cols-2">
                      {sortedFoodItems.map((item) => (
                        <div key={item.id} className="rounded-2xl border border-gray-200 bg-gray-50 p-5">
                          <div className="flex flex-wrap items-start justify-between gap-3">
                            <div>
                              <p className="text-xs font-bold uppercase tracking-wider text-stone-500">
                                {getMealType(item)}
                              </p>
                              <h4 className="mt-2 text-lg font-extrabold text-gray-950">{item.name}</h4>
                            </div>
                            <p className="text-sm font-bold text-amber-700">{formatEstimatedMoney(item.optimizedPrice || item.buyPrice, {}, item)}</p>
                          </div>
                          {item.quantity && <p className="mt-2 text-sm font-semibold text-gray-500">Quantity: {item.quantity}</p>}
                          {item.description && <p className="mt-2 text-sm leading-relaxed text-gray-600">{item.description}</p>}
                        </div>
                      ))}
                    </div>
                  </div>
                ) : (
                  <div className="rounded-2xl border border-dashed border-gray-300 bg-gray-50 p-6 text-sm leading-relaxed text-gray-600">
                    No food or drink items were detected yet. Add menu items to make the plan easier to shop and delegate.
                  </div>
                )}
              </>
            )}
          </div>
        )}

        {activeTab === 'logistics' && (
          <div id="plan-tab-panel-logistics" className={`p-4 sm:p-6 md:p-8 ${isTabAllowed('logistics') ? 'space-y-6' : ''}`}>
            {!isTabAllowed('logistics') ? (
              <NotIncludedState title="Logistics" />
            ) : (
              <>
                <div>
                  <p className="text-xs font-bold uppercase tracking-[0.18em] text-stone-500">Logistics</p>
                  <h3 className="mt-2 text-2xl font-extrabold text-gray-950">Make the plan executable</h3>
                  <p className="mt-2 max-w-2xl text-sm leading-relaxed text-gray-600">
                    This section turns the concept into physical flow, setup ownership, safety checks, and rental decisions.
                  </p>
                </div>
                <div className="grid gap-4 md:grid-cols-2">
                  {logisticsChecklist.map((item) => (
                    <div key={item.title} className="rounded-2xl border border-gray-200 bg-gray-50 p-5">
                      <h4 className="text-lg font-extrabold text-gray-950">{item.title}</h4>
                      <p className="mt-2 text-sm leading-relaxed text-gray-600">{item.body}</p>
                    </div>
                  ))}
                </div>
                <section className="rounded-2xl border border-gray-200 bg-white p-5 sm:p-6">
                  <div className="flex flex-wrap items-center justify-between gap-3">
                    <div>
                      <h4 className="text-lg font-extrabold text-gray-950">What to source</h4>
                      <p className="mt-1 text-sm text-gray-600">
                        Decor, activities, rentals, food &amp; cake, and printables from your shopping list — compare rent vs buy where it saves effort.
                      </p>
                    </div>
                    <button type="button" onClick={() => openCatalogExplorer({ tab: 'items' })} className="h-10 rounded-xl border border-gray-300 px-4 text-sm font-bold text-gray-800 hover:bg-gray-50">
                      Browse catalog
                    </button>
                  </div>
                  <div className="mt-5 grid gap-3 md:grid-cols-2">
                    {categorySpend.slice(0, 6).map((category) => (
                      <div key={category.name} className="rounded-xl border border-gray-200 bg-gray-50 p-4">
                        <p className="font-bold text-gray-950">{category.name}</p>
                        <p className="mt-1 text-sm text-gray-600">
                          {category.count} item{category.count === 1 ? '' : 's'} · {formatEstimatedMoney(category.optimizedTotal)}
                        </p>
                      </div>
                    ))}
                    {categorySpend.length === 0 && (
                      <p className="rounded-xl border border-dashed border-gray-300 p-4 text-sm text-gray-600">
                        Add items in Shopping to build your sourcing list across decor, food, activities, rentals, and printables.
                      </p>
                    )}
                  </div>
                </section>
              </>
            )}
          </div>
        )}

        {activeTab === 'dress' && (
          <div id="plan-tab-panel-dress" className="p-4 sm:p-6 md:p-12">
            {!isTabAllowed('dress') ? (
              <NotIncludedState title="Dress Code" />
            ) : (
              <div className="bg-stone-50 border border-stone-200 rounded-xl p-4 sm:p-6">
                <div className="flex items-center mb-4">
                  <span className="text-2xl mr-3">👗</span>
                  <h3 className="text-lg font-bold text-zinc-900">Suggested Dress Code</h3>
                </div>

                {visibleCount === 0 ? (
                  <div className="text-center py-8 text-zinc-900 border border-dashed border-stone-200 rounded-2xl bg-white p-6">
                    <p className="text-sm font-semibold mb-4">
                      No dress code guidelines have been set yet. Click edit to add custom suggestions.
                    </p>
                    <div className="flex flex-wrap justify-center gap-3">
                      {(() => {
                        const range = getVisibleAgeGroupsByRange();
                        const groupsToEdit = [
                          { key: 'adults', label: 'Adults', emoji: '👔', show: range.showAdults },
                          { key: 'teens', label: 'Teens', emoji: '🎒', show: range.showTeens },
                          { key: 'kids', label: 'Kids', emoji: '🧒', show: range.showKids }
                        ].filter(g => g.show);

                        const finalGroups = groupsToEdit.length ? groupsToEdit : [
                          { key: 'adults', label: 'Adults', emoji: '👔' },
                          { key: 'teens', label: 'Teens', emoji: '🎒' },
                          { key: 'kids', label: 'Kids', emoji: '🧒' }
                        ];

                        return finalGroups.map(g => (
                          <button
                            key={g.key}
                            type="button"
                            onClick={() => {
                              setDressCodeDraft({ ...dressCode });
                              setEditingAgeGroup(g.key);
                            }}
                            className="inline-flex h-10 items-center gap-1.5 px-4 bg-white border border-stone-200 text-zinc-700 text-xs font-bold rounded-xl hover:bg-stone-50 hover:border-stone-300 transition shadow-sm"
                          >
                            <span>{g.emoji}</span>
                            <span>Edit {g.label}</span>
                          </button>
                        ));
                      })()}
                    </div>
                  </div>
                ) : (
                  <div className={`grid grid-cols-1 ${visibleCount === 1 ? 'sm:grid-cols-1' : visibleCount === 2 ? 'sm:grid-cols-2' : 'sm:grid-cols-3'} gap-3 sm:gap-4`}>
                    {[
                      { key: 'adults', label: 'Adults', emoji: '👔', show: showAdults },
                      { key: 'teens', label: 'Teens', emoji: '🎒', show: showTeens },
                      { key: 'kids', label: 'Kids', emoji: '🧒', show: showKids }
                    ].filter((g) => g.show).map((g) => {
                      const group = dressCode?.[g.key] || {};
                      const draftGroup = dressCodeDraft?.[g.key] || {};
                      const isEditing = editingAgeGroup === g.key;
                      const isThemeStyle = (group.style_type === 'theme') && !!group.style_guide;
                      const draftColors = Array.isArray(draftGroup.colors) ? draftGroup.colors : [];
                      const setDraftField = (field, value) => setDressCodeDraft({
                        ...dressCodeDraft,
                        [g.key]: { ...draftGroup, [field]: value }
                      });
                      const setDraftColorAt = (idx, value) => {
                        const next = [...draftColors];
                        next[idx] = value;
                        setDraftField('colors', next.slice(0, 6));
                      };
                      const addDraftColor = () => {
                        if (draftColors.length >= 6) return;
                        setDraftField('colors', [...draftColors, '#cccccc']);
                      };
                      const removeDraftColor = (idx) => {
                        const next = draftColors.filter((_, i) => i !== idx);
                        setDraftField('colors', next);
                      };
                      return (
                        <div key={g.key} className="bg-white rounded-lg p-4 border border-stone-200 flex flex-col relative min-h-[200px]">
                          {isEditing ? (
                            <div className="space-y-2 flex-1">
                              <input
                                type="text"
                                value={draftGroup.style || ''}
                                onChange={(e) => setDraftField('style', e.target.value)}
                                placeholder="Style"
                                className="w-full px-2 py-1 text-sm border border-stone-200 rounded focus:outline-none focus:ring-1 focus:ring-zinc-900"
                              />
                              <textarea
                                value={draftGroup.description || ''}
                                onChange={(e) => setDraftField('description', e.target.value)}
                                placeholder="Description"
                                rows="3"
                                className="w-full px-2 py-1 text-sm border border-stone-200 rounded resize-none focus:outline-none focus:ring-1 focus:ring-zinc-900"
                              />
                              {(draftGroup.style_type === 'theme' || draftGroup.style_guide) && (
                                <textarea
                                  value={draftGroup.style_guide || ''}
                                  onChange={(e) => setDraftField('style_guide', e.target.value)}
                                  placeholder="Style guide (e.g. all-white attire, 90s casual)"
                                  rows="2"
                                  className="w-full px-2 py-1 text-sm border border-stone-200 rounded resize-none focus:outline-none focus:ring-1 focus:ring-zinc-900"
                                />
                              )}
                              <div>
                                <div className="flex items-center justify-between gap-2 mb-2">
                                  <p className="text-[11px] font-semibold uppercase tracking-wide text-gray-500">Suggested colors</p>
                                  <span className="text-[10px] font-semibold text-gray-400">Up to 6</span>
                                </div>
                                <div className="space-y-2">
                                  {draftColors.map((hex, idx) => {
                                    const safeHex = String(hex || '').trim();
                                    const isValid = isValidHex(safeHex);
                                    const colorName = isValid ? getColorNameFromHex(safeHex) : (getColorName(safeHex) || 'Color');
                                    return (
                                      <div key={idx} className="flex items-center gap-2">
                                        <label
                                          className="group relative h-9 w-9 shrink-0 cursor-pointer overflow-hidden rounded-xl border border-gray-200 shadow-inner"
                                          style={{ backgroundColor: isValid ? safeHex : '#ffffff' }}
                                          title="Pick color"
                                        >
                                          <input
                                            type="color"
                                            value={isValid ? safeHex : '#ffffff'}
                                            onChange={(e) => setDraftColorAt(idx, e.target.value)}
                                            className="absolute inset-0 h-full w-full cursor-pointer opacity-0"
                                            aria-label={`Pick color ${idx + 1}`}
                                          />
                                        </label>
                                        <div className="min-w-0 flex-1 leading-tight">
                                          <p className="whitespace-normal break-words text-xs font-semibold text-gray-800">{colorName}</p>
                                          <p className="whitespace-normal break-words font-mono text-[10px] uppercase text-gray-500">{safeHex || '—'}</p>
                                        </div>
                                        <button
                                          type="button"
                                          onClick={() => removeDraftColor(idx)}
                                          className="h-8 w-8 shrink-0 rounded-full text-gray-400 transition hover:bg-red-50 hover:text-red-600"
                                          aria-label={`Remove color ${idx + 1}`}
                                          title="Remove color"
                                        >
                                          <svg className="mx-auto h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 12h12" />
                                          </svg>
                                        </button>
                                      </div>
                                    );
                                  })}
                                  {draftColors.length < 6 && (
                                    <button
                                      type="button"
                                      onClick={addDraftColor}
                                      className="flex min-h-10 w-full items-center justify-center gap-2 rounded-xl border border-dashed border-gray-300 bg-gray-50 text-xs font-bold text-gray-600 transition hover:border-stone-400 hover:bg-stone-50 hover:text-stone-700"
                                    >
                                      <span className="flex h-5 w-5 items-center justify-center rounded-full bg-white text-base leading-none shadow-sm">+</span>
                                      Add color
                                    </button>
                                  )}
                                </div>
                              </div>
                              <div className="flex justify-center gap-2 mt-2">
                                <button onClick={() => setEditingAgeGroup(null)} className="text-xs px-2 py-1 text-gray-500 hover:text-gray-700">Cancel</button>
                                <button onClick={() => { setDressCode(dressCodeDraft); setEditingAgeGroup(null); }} className="text-xs px-2 py-1 text-zinc-900 font-bold hover:text-zinc-600">Save</button>
                              </div>
                            </div>
                          ) : (
                            <>
                              <div className="flex justify-center mb-2">
                                <span className="text-2xl">{g.emoji}</span>
                              </div>
                              <div className="text-center mb-2">
                                <span className="font-semibold text-zinc-900">{g.label}</span>
                              </div>
                              <div className="text-center mb-2">
                                <p className="text-sm font-bold text-gray-900">{group.style || ''}</p>
                              </div>
                              <div className="text-center mb-3 flex-1 space-y-2">
                                {group.description && (
                                  <p className="text-xs text-gray-600 leading-relaxed">{softenDressCodeText(group.description)}</p>
                                )}
                                {isThemeStyle && (
                                  <p className="text-xs text-gray-700 leading-relaxed italic">
                                    {softenDressCodeText(group.style_guide)}
                                  </p>
                                )}
                              </div>
                              {!isThemeStyle && (
                                <div className="flex flex-wrap justify-center gap-x-2 gap-y-3 mb-2">
                                  {(Array.isArray(group.colors) && group.colors.length ? group.colors : dressPaletteHexes).map((hex, idx) => (
                                    <div key={`${hex}-${idx}`} className="flex w-[4.5rem] flex-col items-center">
                                      <div className="h-7 w-7 rounded-full ring-1 ring-gray-300 shadow-sm" style={{ backgroundColor: hex }} title={getColorName(hex)} />
                                      <span className="mt-1 text-center text-[10px] leading-tight text-gray-500 break-words">{getColorName(hex)}</span>
                                    </div>
                                  ))}
                                </div>
                              )}
                              <div className="absolute bottom-2 right-2">
                                <button
                                  onClick={() => {
                                    setDressCodeDraft({ ...dressCode });
                                    setEditingAgeGroup(g.key);
                                  }}
                                  className="text-xs text-zinc-500 hover:text-zinc-900"
                                  style={{ transform: 'scaleX(-1)' }}
                                  aria-label={`Edit ${g.label} dress code`}
                                >
                                  ✏️
                                </button>
                              </div>
                            </>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {activeTab === 'colors' && (
          <div id="plan-tab-panel-colors" className="p-4 sm:p-6 md:p-12">
            {!isTabAllowed('colors') ? (
              <NotIncludedState title="Colors" />
            ) : displayedPalette.length === 0 ? (
              <MissingDataState title="Colors" />
            ) : (
              <div className="bg-gray-50 rounded-xl p-6 border border-gray-200">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center">
                    <span className="text-2xl mr-3">🎨</span>
                    <h3 className="text-lg font-bold text-gray-800">
                      Color Palette
                    </h3>
                  </div>
                  {!isSharedView && (
                    <button
                      type="button"
                      onClick={() => setShowPaletteEditModal(true)}
                      className="rounded-lg border border-stone-200 bg-stone-50 px-3 py-1.5 text-sm font-semibold text-stone-700 transition hover:bg-stone-100"
                    >
                      Edit colors
                    </button>
                  )}
                </div>
                <p className="text-sm text-gray-500 mb-4">
                  Up to 6 colors. Use Edit colors to add, rename, pick hex values, or remove colors.
                </p>
                <div className="flex flex-wrap gap-3 sm:gap-4">
                  {displayedPalette.map((color, idx) => {
                    const colorName = color.name || color.hex;
                    const words = colorName ? colorName.trim().split(/\s+/) : [];
                    const hasMultipleWords = words.length > 1;

                    // Smart splitting for multi-word names
                    const getSplitWords = () => {
                      if (words.length === 1) return { first: words[0], second: null };
                      if (words.length === 2) return { first: words[0], second: words[1] };

                      // For 3+ words, find the best split point based on string length
                      const totalLength = words.join(' ').length;
                      const targetLength = Math.floor(totalLength / 2);

                      let currentLength = 0;
                      let splitIndex = 1;

                      // Find the split point that balances the two lines
                      for (let i = 0; i < words.length - 1; i++) {
                        const lineLength = words.slice(0, i + 1).join(' ').length;
                        if (Math.abs(lineLength - targetLength) < Math.abs(currentLength - targetLength)) {
                          splitIndex = i + 1;
                          currentLength = lineLength;
                        }
                      }

                      return {
                        first: words.slice(0, splitIndex).join(' '),
                        second: words.slice(splitIndex).join(' ')
                      };
                    };

                    const { first, second } = getSplitWords();

                    return (
                      <div key={`${color.hex}-${idx}`} className="flex min-w-0 flex-col items-center">
                        <div
                          className="h-12 w-12 shrink-0 rounded-xl border border-gray-200 shadow-md sm:h-14 sm:w-14"
                          style={{ backgroundColor: color.hex }}
                          title={colorName}
                        />
                        <span className="mt-2 w-full px-0.5 text-center text-[11px] leading-tight text-gray-600 sm:text-xs whitespace-normal break-words max-w-full">
                          {hasMultipleWords ? (
                            <>
                              {first}
                              <br />
                              {second}
                            </>
                          ) : (
                            colorName
                          )}
                        </span>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        )}
          </>
        )}
      </div>
        </div>
      </div>

      {/* Why We Chose This Theme Modal */}
      {showReasoningModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" onClick={() => setShowReasoningModal(false)}>
          <div className="bg-white rounded-xl shadow-xl max-w-2xl w-full p-6 md:p-8 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-start justify-between gap-4 mb-4">
              <div className="flex items-center gap-3">
                <span className="text-2xl flex-shrink-0">💡</span>
                <h3 className="text-xl font-bold text-gray-900">
                  Why This {getConceptLabelForRationale()} Fits Your Event
                </h3>
              </div>
              <ModalCloseButton onClick={() => setShowReasoningModal(false)} />
            </div>

            <p className="text-sm leading-relaxed text-gray-700 mb-5">
              {isThemeIdeas ? (
                `Based on your theme details${eventData?.age_range ? ` and ${eventData.age_range} audience` : ''} and ${eventTypeDescriptive} type, we curated a creative direction to give you the perfect starting spark.`
              ) : (
                `Based on your ${hasProvidedBudget ? `${budgetSummary} budget` : 'planning details'} for ${hasProvidedGuestCount ? `${eventData.guest_count} guests` : 'your guests'} (${perGuestSummary ? `~${perGuestSummary}/person` : 'budget-aware planning'}${eventData?.age_range ? `, ${eventData.age_range} audience` : ''}) and ${eventTypeDescriptive} type, we focused on the constraints that usually decide whether a plan is actually usable.`
              )}
            </p>

            <div className="space-y-3 mb-6">
              {fitInsights.map((insight) => (
                <div key={insight.label} className="rounded-xl border border-gray-200 bg-gray-50 p-4">
                  <p className="text-sm font-bold text-gray-950">{insight.label}</p>
                  <p className="mt-1 text-sm leading-relaxed text-gray-700">{insight.body}</p>
                </div>
              ))}
            </div>

            <div className="mb-6 rounded-xl border border-amber-200 bg-amber-50 p-4 text-sm leading-relaxed text-amber-900">
              Treat this as planner reasoning, not a guarantee. Verify current prices, availability, safety requirements, dietary needs, and venue rules before purchasing or hosting.
            </div>
          </div>
        </div>
      )}

      {showCatalogModal && (
        <CatalogExplorer
          isOpen={showCatalogModal}
          onClose={() => setShowCatalogModal(false)}
          onSelectItem={(catalogItem) => {
            const wasAdded = addCatalogItemToPlan(catalogItem);
            if (wasAdded !== false) setActiveTab('shopping');
            return wasAdded;
          }}
          eventData={eventData}
          initialTab={catalogLaunch.tab}
          initialCategory={catalogLaunch.category}
          initialSearch={catalogLaunch.search}
          existingItems={items}
        />
      )}

      <ColorPaletteEditModal
        isOpen={showPaletteEditModal}
        onClose={() => setShowPaletteEditModal(false)}
        onSave={handlePaletteSave}
        initialPalette={colorPalette}
        title="Edit colors"
        minColors={3}
        removeConfirmDescription="will be removed from the plan palette."
      />

      <PlanEventDetailsModal
        isOpen={showEventDetailsModal}
        onClose={() => setShowEventDetailsModal(false)}
        eventData={eventData}
        plannedThemeCount={plannedThemeEntries.length}
        onSaveDetails={(updates) => {
          onSaveCoreEventDetails?.(updates);
          setShowEventDetailsModal(false);
        }}
        onReEstimate={(updates) => {
          onReEstimatePlan?.(updates);
          setShowEventDetailsModal(false);
        }}
      />

      <DigitalInviteModal
        isOpen={showInviteModal}
        onClose={() => setShowInviteModal(false)}
        theme={theme}
        eventData={eventData}
      />

      <BulkCartExportModal
        isOpen={showBulkCartModal}
        onClose={() => setShowBulkCartModal(false)}
        items={items}
        theme={theme}
        eventData={eventData}
      />

      <SocialStoryModal
        isOpen={showSocialStoryModal}
        onClose={() => setShowSocialStoryModal(false)}
        theme={theme}
        eventData={eventData}
      />
    </div>
  );
};

export default Plan;

if (typeof window !== 'undefined') {
  window.addEventListener('popstate', () => {
    setTimeout(() => {
      const isPlanPage = window.location.pathname.includes('/wizard/plan') || window.location.hash.includes('/wizard/plan');
      if (isPlanPage && !window.__planComponentMounted) {
        if (typeof window.__onViewThemePlan === 'function' && window.__currentTheme) {
          window.__onViewThemePlan(window.__currentTheme);
        } else {
          window.location.reload();
        }
      }
    }, 50);
  });
}
